import {
  showKycUnavailableAlert,
  surfaceFailureReason,
} from '../fiat/kycUnavailableAlert'
import type {
  EntryCta,
  FailureReason,
  FiatFlow,
  FormValues,
  KycOutcome,
  Quote,
  RouteIdentity,
  SubmitTransition,
  Surface,
  SurfaceResult,
  SurfaceTransition,
  TermsToAccept,
  UseFiatQuoteResult,
} from '@funkit/connect-core'
import { useQueryClient } from '@tanstack/react-query'
import {
  DEFAULT_FIAT_CURRENCY,
  acceptedTerms,
  enteredStep,
  forgetRouteLimits,
  isQuotableAmount,
  isStaleQuoteFailure,
  renewCaptureSurface,
  requestCheckoutClientSecret,
  selectEntryCta,
  selectKycOutcome,
  shouldRenderCta,
  submittableTransition,
  surfaceControl,
  termsToAccept,
  useEffectEvent,
  useFiatQuote,
  useFiatTransitions,
  useSwappedDefaultCurrencyQuery,
} from '@funkit/connect-core'
import type { TFunction } from 'i18next'
import {
  Fragment,
  type ReactElement,
  type ReactNode,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { StyleSheet, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { useFunkitBranding } from '../branding/BrandingContext'
import { PillButton } from '../components/PillButton'
import { entryCtaLabel, isIdentityDetour } from '../fiat/ctaLabel'
import {
  captureControl,
  renderCaptureSurface,
} from '../fiat/renderCaptureSurface'
import { sumsubKycAdapter } from '../fiat/providers/sumsubKyc'
import { ScreenSurfaceCover } from '../fiat/ScreenSurfaceCover'
import { StripeSessionAuthController } from '../fiat/providers/StripeSessionAuthController'
import { stripeLinkRegistrationFrom } from '../fiat/providers/stripeAccountCreation'
import { stripeKycInfoFrom } from '../fiat/providers/stripeKycInfo'
import { stripeAuthorizeAdapter } from '../fiat/providers/stripeAuthorize'
import {
  STRIPE_KYC_CANCELLED,
  stripeKycAdapter,
} from '../fiat/providers/stripeKyc'
import { stripeCheckoutAdapter } from '../fiat/providers/stripeCheckout'
import {
  STRIPE_PAYMENT_CANCELLED,
  stripePaymentAdapter,
} from '../fiat/providers/stripePayment'
import { stripeSessionAuthStart } from '../fiat/providers/stripeSessionAuth'
import { SurfaceSlot } from '../fiat/SurfaceSlot'
import { FiatContactScreen } from '../fiat/FiatContactScreen'
import { FiatIdentityStep } from '../fiat/FiatIdentityStep'
import { FiatOtpContent, FiatOtpScreen } from '../fiat/FiatOtpScreen'
import { useOtpReceipt } from '../fiat/useOtpReceipt'
import {
  FiatFailureBanner,
  type FiatFailureBannerProps,
} from '../fiat/FiatFailureBanner'
import { methodDisplayName } from '../fiat/methodName'
import { FiatKycNotice } from '../fiat/FiatKycNotice'
import { FiatKycOptionsScreen } from '../fiat/FiatKycOptionsScreen'
import type { FiatKycOptionRowProps } from '../fiat/FiatKycOptionRow'
import { openKycSupport } from '../fiat/kycSupport'
import { FiatKycCompletion } from '../fiat/FiatKycOutcomeScreen'
import { FiatRegionUnsupportedScreen } from '../fiat/FiatRegionUnsupportedScreen'
import { useKycCompletion } from '../fiat/useKycCompletion'
import { useQuoteableCeiling } from '../fiat/useQuoteableCeiling'
import { isAboveMaximum, isBelowMinimum } from '../hooks/useGuardedAmountInput'
import { useFlowStepFrame } from '../hooks/useNestedSheetHeight'
import { StepPage } from './StepPage'
import { useTheme } from '../theme/restyle'
import { isDarkColor } from '../theme/isDarkColor'
import { FiatActionButton } from '../fiat/FiatActionButton'
import { FiatDocumentCaptureStep } from '../fiat/FiatDocumentCaptureStep'
import {
  COINBASE_USER_AGREEMENT_URL,
  FiatTermsNotice,
} from '../fiat/FiatTermsNotice'
import { useFiatCheckoutPreparation } from '../fiat/useFiatCheckoutPreparation'
import { useFiatDirectDelivery } from '../fiat/useFiatDirectDelivery'
import {
  type SessionContactField,
  isUnfamiliarContactName,
  sessionContactFields,
} from '../fiat/sessionContact'
import type { KnownContacts } from '../fiat/knownContacts'
import { rememberContact, resolveContacts } from '../fiat/knownContacts'
import { formScreenProps } from '../fiat/formScreenProps'
import { useReplayableFailure } from '../fiat/useReplayableFailure'
import type { StaleQuoteRecovery } from '../fiat/useStaleQuoteRecovery'
import { useStaleQuoteRecovery } from '../fiat/useStaleQuoteRecovery'
import { RESEND_ID, otpScreenProps } from '../fiat/otpScreenProps'
import { FiatPendingOrderScreen } from '../fiat/FiatPendingOrderScreen'
import { FiatStatusScreen } from '../fiat/FiatStatusScreen'
import {
  blockedRetryDetail,
  isExpiredQuoteMarker,
  kycHoldRetryDetail,
  providerName,
  quoteFee,
  quoteFeeRows,
  quoteFeeSummary,
  recoveryCta,
  reviewDetail,
  roundErrorMessage,
} from '../fiat/stepDetails'
import { useUserIpInfo } from '../geo/useUserIpInfo'
import { useFunkitIdentity } from '../identity/IdentityContext'
import { useIdentityAssertion } from '../fiat/useIdentityAssertion'
import { logger } from '../logging/logger'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { useStripeOnramp } from '../providers/NativeCapabilitiesContext'
import { DepositAmountScreen } from '../screens/DepositAmountScreen'
import { DEPOSIT_METRICS } from '../screens/depositMetrics'
import type { FeeDetailsRow } from '../screens/FeeDetailsScreen'
import {
  formatFiat,
  formatFiatWhole,
  groupThousands,
} from '../utils/formatAmount'
import type {
  DepositAmountEntryConfig,
  DepositCheckoutConfig,
  DepositClientMetadata,
  DepositFlowCallbacks,
  DepositInitialAmount,
  DepositMethod,
  FiatRailConfig,
} from './depositFlowConfig'
import type { DepositReceipt } from './useDepositReceivedToast'
import {
  type DepositOutcomeReporter,
  useDepositOutcome,
  useDepositOutcomeReporter,
} from '../fiat/depositOutcome'
import { flowError } from './flowError'
import { accountBalanceChipProps } from './accountContext'
import { resolveDepositRailCopy } from './depositRailCopy'
import { FeeDetailsSheet, useFeeDetailsSheet } from './FeeDetailsSheet'
import {
  contactRefusal,
  hasOverLimitCeiling,
  isOverLimit,
  quoteRefusal,
  showPaymentUnavailableAlert,
  showQuoteRefusalAlert,
} from '../fiat/quoteRefusal'
import { useDynamicConfig } from '../statsig/useDynamicConfig'
import { useLockFlowDismiss } from './FlowDismissLock'

const DEFAULT_QUICK_AMOUNTS = ['10', '20', '50', '100']

declare const __DEV__: boolean | undefined
const IS_DEV_BUILD = typeof __DEV__ !== 'undefined' && !!__DEV__

const noop = () => undefined

/** Figma 2812:1135, measured on the 393 × 852 reference frame. */
const FAILURE_BANNER_TOP = 56

/** A provider that gave up, and the sentence it left the user with. */
interface SurfaceFailure {
  code: string
  detail?: string
}

const SURFACE_MATCHED_SIZE = 'buttonLg' as const

/** Everything the contact screen needs, read once so a refetch cannot move it. */
interface ContactRound {
  fields: readonly SessionContactField[]
  transition: SubmitTransition
  terms: TermsToAccept | undefined
}

/** `undefined` when the entry has nothing to collect and the tap just fires. */
function contactRoundToAsk(
  fields: readonly SessionContactField[],
  entry: EntryCta,
  terms: TermsToAccept | undefined,
): ContactRound | undefined {
  const transition = submittableTransition(entry)
  if (fields.length === 0 || transition === undefined) {
    return undefined
  }
  return { fields, transition, terms }
}

/**
 * Screens with no implementation, which render the unsupported notice whatever
 * the response carried — a state this build's contract version predates. A
 * capture screen whose surface no adapter claims is reported by
 * `capture_surface_unsupported` instead, which can name the provider.
 */
const UNDRAWN_SCREENS = new Set<FiatFlow['page']['screen']>(['UNSUPPORTED'])

/**
 * Pages a paid deposit only waits on, which the amount screen holds instead of
 * drawing. Just these two: `ORDER_REVIEW` and `ORDER_PENDING` also follow an
 * order, but each asks the user for something — confirm, resume, cancel — and
 * a wait drawn over them is a button nobody can press.
 */
const WAITS_ON_PAYMENT = new Set<FiatFlow['page']['screen']>([
  'PAYMENT_CAPTURE',
  'ORDER_TRACKING',
])

/**
 * Whether a surface report is the payment. Both order topologies land on the
 * order route — `POST /fiat/orders` where the capture makes the order,
 * `POST /fiat/orders/:id/surface-result` where the order was made first, as
 * Coinbase's is. A KYC capture reports elsewhere and must not count.
 */
const reportsPayment = (endpoint: string): boolean =>
  /^POST \/fiat\/orders(\/|$)/.test(endpoint)

/** Shown when Coinbase priced the screen but no tap on it accepts anything. */
const COINBASE_STANDING_TERMS = { documentUrl: COINBASE_USER_AGREEMENT_URL }

export function FiatDepositAmountStep({
  config,
  fiat,
  checkoutConfig,
  dynamicRoutingId,
  depositAddressOverride,
  checkoutId,
  clientMetadata,
  initialAmount,
  lockAmount,
  callbacks,
  showDepositToast,
  onCloseFlow,
}: {
  config: DepositAmountEntryConfig
  fiat: FiatRailConfig
  checkoutConfig: DepositCheckoutConfig | undefined
  dynamicRoutingId: string | undefined
  /** Host fallback for local/dev clients that do not mint an EOA. */
  depositAddressOverride?: string
  checkoutId: string
  clientMetadata: DepositClientMetadata | undefined
  /**
   * Prefills the quoted amount.
   */
  initialAmount?: DepositInitialAmount
  /** Locks the prefilled amount against numpad and quick-amount edits. */
  lockAmount?: boolean
  callbacks: Pick<DepositFlowCallbacks, 'onDepositSubmitted' | 'onDepositError'>
  /** Raises the SDK's deposit confirmation, which outlives this screen. */
  showDepositToast: (receipt: DepositReceipt) => void
  onCloseFlow: () => void
}) {
  const { t } = useFunkitTranslation()
  const outcome = useDepositOutcomeReporter({
    callbacks,
    checkoutId,
    paymentMethod: fiat.paymentMethod,
    t,
  })
  const [amount, setAmount] = useState(initialAmount?.value ?? '')
  const [flowRun, setFlowRun] = useState(0)
  const [entryScreen, setEntryScreen] = useState<ReactNode>(null)
  const onBackToEntry = useCallback(() => setFlowRun((n) => n + 1), [])
  const { height } = useFlowStepFrame()
  // The amount page is already up when the sheet opens; only a return to it is
  // a turn, and an entrance there would fade the whole step in behind the
  // sheet's own arrival.
  const hasTurned = useRef(false)
  useEffect(() => {
    if (entryScreen === null) {
      hasTurned.current = true
    }
  }, [entryScreen])

  // A frame both pages can fill: while a turn runs they stand in it together,
  // and stacked in flow the arriving one would draw a screen below the fold.
  return (
    <View style={{ height }}>
      {entryScreen === null ? null : (
        <StepPage side="behind" animateIn={hasTurned.current}>
          {entryScreen}
        </StepPage>
      )}
      {/* After the entry, so the page ahead turns over it rather than under. */}
      <FiatFlowSession
        key={flowRun}
        config={config}
        fiat={fiat}
        checkoutConfig={checkoutConfig}
        dynamicRoutingId={dynamicRoutingId}
        depositAddressOverride={depositAddressOverride}
        checkoutId={checkoutId}
        clientMetadata={clientMetadata}
        amount={amount}
        onChangeAmount={setAmount}
        lockAmount={lockAmount}
        outcome={outcome}
        onBackToEntry={onBackToEntry}
        showDepositToast={showDepositToast}
        onCloseFlow={onCloseFlow}
        onEntryScreen={setEntryScreen}
      />
    </View>
  )
}

// Reset the session's requests and local state without replaying the screen's
// glyph entrances. Publish before paint; memo skips the parent's screen update.
const FiatFlowSession = memo(function FiatFlowSession({
  onEntryScreen,
  ...props
}: FiatFlowSessionProps & { onEntryScreen: (screen: ReactNode) => void }) {
  const screen = useFiatFlowScreen(props)
  const entry = screen.key === 'amount-entry' ? screen : null
  useLayoutEffect(() => {
    onEntryScreen(entry)
  }, [onEntryScreen, entry])
  return entry === null ? (
    <StepPage key={pageKey(screen)} side="ahead">
      {screen}
    </StepPage>
  ) : null
})

// An id per screen component, minted on first sight. The step draws several
// pages of its own — the phone number, then the code sent to it — and each
// has to be its own page for the one leaving and the one arriving to cross.
const pageIds = new Map<unknown, number>()

/**
 * What makes two renders the same page: the component drawing it, and the key
 * it carries when it has one. Props changing is the page speaking, not a turn.
 */
function pageKey(screen: ReactElement): string {
  let id = pageIds.get(screen.type)
  if (id === undefined) {
    id = pageIds.size
    pageIds.set(screen.type, id)
  }
  return screen.key === null ? `page-${id}` : `page-${id}-${screen.key}`
}

interface FiatFlowSessionProps {
  config: DepositAmountEntryConfig
  fiat: FiatRailConfig
  checkoutConfig: DepositCheckoutConfig | undefined
  dynamicRoutingId: string | undefined
  depositAddressOverride?: string
  checkoutId: string
  clientMetadata: DepositClientMetadata | undefined
  amount: string
  onChangeAmount: (amount: string) => void
  lockAmount?: boolean
  outcome: DepositOutcomeReporter
  onBackToEntry: () => void
  showDepositToast: (receipt: DepositReceipt) => void
  onCloseFlow: () => void
}

// One switch over the flow state, grown past the cap by the KYC arms and the
// redirect handoff. Splitting it is ENG-5902, not this PR.
// eslint-disable-next-line max-lines-per-function
function useFiatFlowScreen({
  config,
  fiat,
  checkoutConfig,
  dynamicRoutingId,
  depositAddressOverride,
  checkoutId,
  clientMetadata,
  amount,
  onChangeAmount,
  lockAmount,
  outcome,
  onBackToEntry,
  showDepositToast,
  onCloseFlow,
}: FiatFlowSessionProps) {
  const { t, locale } = useFunkitTranslation()
  const theme = useTheme()
  const alertScheme = isDarkColor(theme.background.modal) ? 'dark' : 'light'
  const stripeOnramp = useStripeOnramp()
  const { apiKey, userId, getIdentityAssertion, prefillFiatEmail } =
    useFunkitIdentity()
  const { userIpInfo } = useUserIpInfo()
  const { hideAttribution } = useFunkitBranding()
  // 2812:1135 puts the failure banner at the reference frame's own top inset,
  // so the device's inset carries it rather than a fixed offset.
  const bannerTop =
    useSafeAreaInsets().top +
    FAILURE_BANNER_TOP -
    DEPOSIT_METRICS.reference.insetTop
  const { sheet, openFeeDetails: presentFeeDetails } = useFeeDetailsSheet()
  const [feeRows, setFeeRows] = useState<FeeDetailsRow[]>([])
  const [feeSummary, setFeeSummary] = useState<string>()

  const { identityAssertion, assertedEmail, accountIneligible } =
    useIdentityAssertion({
      getIdentityAssertion,
      userId,
      outcome,
      onCloseFlow,
      t,
    })

  // Delivers to the recipient when the rail buys the destination asset, and to
  // a generated address when something still has to swap it.
  const { depositAddress, isDirect, depositAddressError, retryDepositAddress } =
    useFiatDirectDelivery({
      checkoutConfig,
      rail: fiat,
      dynamicRoutingId,
      checkoutId,
      clientMetadata,
      depositAddressOverride,
      resolveOnBackend: identityAssertion !== undefined,
    })

  // A provider machine that errored is terminal. Where the server offers no
  // edge that mints a replacement, recovery is a fresh mount rather than a
  // retried call, and the attempt counter is what rebuilds it.
  const [surfaceFailure, setSurfaceFailure] = useState<SurfaceFailure>()
  const failSurface = (code: string, detail?: string) => {
    setSurfaceFailure({ code, ...(detail === undefined ? {} : { detail }) })
  }
  const [surfaceAttempt, setSurfaceAttempt] = useState(0)
  // A provider's own button warms up after the quote lands, behind the slot's
  // cover. Nothing waits on a slot that is not drawn, so this starts ready.
  const [isSurfaceReady, setSurfaceReady] = useState(true)
  // The number or address is collected before the transition that opens the
  // session, not by a state the server drives — so the step is this screen's
  // own.
  //
  // Pinned when the screen opens rather than read live: the quote query holds
  // no response while it refetches, so the round behind the form can vanish
  // mid-typing and take its fields, its terms and its transition with it.
  const [contactRound, setContactRound] = useState<ContactRound>()
  const [onKycOptions, setOnKycOptions] = useState(false)
  // Consent is the tap, so the moment is stamped there — but it has to reach
  // the reducer before the transition is sent, which is the render after it.
  const [isAccepting, setIsAccepting] = useState(false)
  const firing = useRef<SubmitTransition | undefined>(undefined)
  const [acceptedNotice, setAcceptedNotice] = useState<TermsToAccept>()
  // Held here rather than read back off the flow: `collected` belongs to the
  // round that gathered it and is cleared once the step moves on, but each
  // code round still has to name where its own code went.
  const [collected, setCollected] = useState<KnownContacts>({})
  // Resolved every render, not captured at mount: a host that swaps users
  // while the screen stands replaces the address with it, and a value held
  // from mount would name the previous user's.
  //
  // The host's address counts only alongside an assertion. That is the one
  // this rail mails, since the assertion is what declares the user to it. A
  // host supplying none is on the Swapped ramp, where the field is a prefill
  // and says nothing about where a code went.
  //
  // The assertion's own `email` claim is what the rail actually mails, so it
  // outranks the host's prefill once one has been minted.
  const contacts = resolveContacts(
    getIdentityAssertion === undefined
      ? undefined
      : (assertedEmail ?? prefillFiatEmail),
    collected,
  )

  const flow = useFiatTransitions({
    apiKey,
    logger,
    identityAssertion,
    depositAddress,
    // Nothing follows the provider's transfer on this route, so the order is
    // done when it lands — the user is not held on a spinner waiting for the
    // backend to agree.
    settlesOnDelivery: isDirect,
  })
  useDepositOutcome(flow, outcome, t)
  const otpReceipt = useOtpReceipt(flow)
  // Whether recovery is the server's to hand out, read by the same id the code
  // screen reads. Only a round that carries it can be replaced.
  const resend = flow.ctas.find((transition) => transition.id === RESEND_ID)
  const retrySurface = () => {
    setSurfaceFailure(undefined)
    if (resend === undefined) {
      setSurfaceAttempt((n) => n + 1)
      return
    }
    // The tap is the acceptance, exactly as the entry CTA's tap is one — the
    // provider re-asserts its terms on every create. Carried on the submit
    // because `setField` lands a render late, and the round is sent on this one.
    const accept = termsToAccept({
      kind: 'AUTHENTICATE',
      control: { mode: 'SUBMIT', cta: resend },
    })
    // The attempt counter stays put: the round that lands brings its own
    // surface, and bumping it here would rebuild the dead one mid-flight and
    // let it report the same failure a second time.
    void flow.submit(
      resend,
      accept === undefined
        ? undefined
        : { [accept.name]: acceptedTerms(accept, new Date()) },
    )
  }
  // The buyer's region decides what they pay in, resolved the same way the
  // withdrawal rail resolves it. The rail refuses a currency the region does
  // not use — `USD` from an AU buyer is priced by nobody — so the lookup's
  // answer is the request, not a default to fall back from.
  const {
    data: regionCurrency,
    isLoading: isRegionCurrencyLoading,
    isError: isRegionCurrencyFailed,
  } = useSwappedDefaultCurrencyQuery({
    apiKey,
    countryCode: userIpInfo?.alpha2,
    logger,
  })
  // Wait while it is in flight, but never block a deposit on it. `isLoading`
  // rather than `isPending`: with no country the lookup never runs, and a query
  // that never runs is pending forever.
  // A lookup that failed outright names no currency, and quoting the default
  // against a country it does not belong to asks for a price nobody can
  // serve — so it waits rather than quoting. A country the lookup covers but
  // has no entry for still falls back, which is the lookup's own default.
  const quoteCurrency =
    isRegionCurrencyLoading || isRegionCurrencyFailed
      ? undefined
      : (regionCurrency ?? DEFAULT_FIAT_CURRENCY)

  // Forwarded in the geo endpoint's own field names — the quote endpoint takes
  // the lookup's answer, and a US buyer is priced against their state.
  const buyerGeo =
    userIpInfo === undefined
      ? undefined
      : { alpha2: userIpInfo.alpha2, region: userIpInfo.state }

  // Shared with web's checkout, where the same key caps the amount input.
  const maxCheckoutUsd = useDynamicConfig('maxcheckoutusd')
  const configuredLimitUsd =
    maxCheckoutUsd > 0 ? String(maxCheckoutUsd) : undefined

  // A cap somebody set deliberately: the host's own figure, else the Statsig
  // one. There is no stand-in when neither is set — an invented ceiling is a
  // second, wrong answer to "what is the most I can buy", and the buyer meets
  // it exactly when the real one is out of reach (ENG-6302).
  const configuredMax = config.limitUsd ?? configuredLimitUsd

  // The same route the quote describes, so the two cannot disagree about which
  // one is being priced.
  const quoteRoute: RouteIdentity = {
    apiKey,
    userId,
    currency: quoteCurrency,
    paymentMethod: fiat.paymentMethod,
    cryptoCurrencyCode: fiat.cryptoCurrencyCode,
    chainId: fiat.chainId,
    geo: buyerGeo,
    routingOverrides: fiat.unstable_routingOverrides,
  }
  const quoteableCeiling = useQuoteableCeiling({
    route: quoteRoute,
    logger,
    getIdentityAssertion,
  })
  // The entered amount is the quote's `fiatAmount`, so it is denominated in
  // `quoteCurrency`. A ceiling under another symbol is dropped rather than
  // converted: comparing across two would refuse a purchase that would serve.
  const preloadedMax =
    quoteableCeiling !== undefined &&
    quoteableCeiling.currency === quoteCurrency
      ? quoteableCeiling.max
      : undefined

  // The tightest cap anyone has named. The configured ones are read in
  // `quoteCurrency` like every other figure on this screen, which is what the
  // host sets them against.
  const clientMaxUsd =
    preloadedMax !== undefined &&
    (configuredMax === undefined ||
      Number(preloadedMax) < Number(configuredMax))
      ? preloadedMax
      : configuredMax

  // An amount outside either bound cannot be priced, so the request is held
  // back rather than buying a refusal the buyer meets as a modal. Both verdicts
  // are immediate, so there is nothing to wait on before deciding.
  //
  // The ceilings gated on here are the ones known without asking. The
  // corridor's own arrives on a price and would gate the request that fetches
  // it, so with none of them in hand every amount is priced.
  const belowMinimum = isBelowMinimum(amount, config.minLimitUsd)
  const aboveMaximum = isAboveMaximum(amount, clientMaxUsd)

  const quoteState = useFiatQuote({
    apiKey,
    userId,
    logger,
    flow,
    amount,
    currency: quoteCurrency,
    paymentMethod: fiat.paymentMethod,
    cryptoCurrencyCode: fiat.cryptoCurrencyCode,
    chainId: fiat.chainId,
    geo: buyerGeo,
    unstable_routingOverrides: fiat.unstable_routingOverrides,
    // Off once the flow holds a round: a refetch mints a new quoteRef, and the
    // session just verified against the current one would be orphaned by it.
    enabled: flow.stepResponse === undefined && !belowMinimum && !aboveMaximum,
  })

  const refusal = quoteRefusal(flow.stepResponse)
  // Silent only when the field can name a cap under the entered amount; see
  // `hasOverLimitCeiling`. Kept as a boolean because the objects behind it are
  // rebuilt each render, and the alert must raise once per response.
  const overLimitSilenced =
    refusal !== undefined &&
    isOverLimit(refusal) &&
    hasOverLimitCeiling(flow.stepResponse, quoteState.maximum, amount)
  const flowQuoteExpired =
    refusal?.category === 'EXPIRED' && refusal.recovery === 'REQUOTE'
  const recoverExpiredFlowQuote = useEffectEvent(() => {
    logger.info('connect-rn:fiat:flow_quote_expired_requoting', {
      provider: flow.stepResponse?.provider,
    })
    // A flow-owned quote cannot be refreshed in place: its quoteRef names the
    // accepted terms and every transition is bound to it. Rebuild the session
    // while preserving the entered amount so useFiatQuote obtains a fresh ref.
    onBackToEntry()
  })
  useEffect(() => {
    if (flowQuoteExpired) {
      recoverExpiredFlowQuote()
    }
  }, [flowQuoteExpired])

  const quotedRound = enteredStep(quoteState.entryCta)
  const blockedStep = flow.stepResponse ?? quotedRound
  // Whichever response carried the check: the round the flow entered, or the
  // quote answered with the check instead of a price.
  const resolvedStep = blockedStep
  const blockedState =
    blockedStep?.state.kind === 'BLOCKED' ? blockedStep.state : undefined
  const paymentUnavailable = blockedState?.reason === 'ALL_PROVIDERS_DECLINED'
  const announceBlocked = useEffectEvent(() => {
    if (blockedState === undefined) {
      return
    }
    logger.info('connect-rn:fiat:blocked', {
      reason: blockedState.reason,
      retryAfter: blockedState.retryAfter,
      provider: blockedStep?.provider,
    })
    if (!paymentUnavailable) {
      return
    }
    showPaymentUnavailableAlert(
      t,
      fiat.paymentMethod === 'apple_pay',
      alertScheme,
    )
    // A pre-quote refusal holds no price; keep it until the buyer retries.
    // Resetting it here would immediately fetch and announce the same refusal.
    if (flow.stepResponse !== undefined) {
      quoteState.dropQuote()
    }
  })
  useEffect(() => {
    announceBlocked()
  }, [blockedState])

  // Whether a pay sheet has reported its completion to the order route. From
  // then on the card has been charged, so no failure may ask for a new price:
  // a fresh quote opens a fresh sheet, and that is a second charge. Read off
  // the transition rather than the result's keys, which each rail names
  // differently.
  const [capturedPayment, setCapturedPayment] = useState(false)
  const reportSurface = useCallback(
    async (result: SurfaceResult, transition?: SurfaceTransition) => {
      const surface = transition ?? flow.surface
      if (surface !== undefined && reportsPayment(surface.report.endpoint)) {
        setCapturedPayment(true)
      }
      try {
        await flow.reportSurface(result, transition)
      } finally {
        // After the await, so the transition records the report emits are in
        // the batch. The timer would otherwise strand exactly the records
        // worth reading when the app is killed on a failed attempt.
        logger.flush()
      }
    },
    [flow],
  )
  // Once the card is charged, a failure the payment page reports is not the
  // order's to report: the order poll owns the outcome, and "Try again" here
  // would draw a second Apple Pay button over money already taken.
  const failPaymentSurface = (code: string, detail?: string) => {
    if (!capturedPayment) {
      failSurface(code, detail)
      return
    }
    logger.warn('connect-rn:fiat:surface_failed_after_capture', {
      code,
      provider: flow.stepResponse?.provider,
      screen: flow.page.screen,
    })
  }

  // A dead ref on the entry screen is replaced and re-sent in place: the
  // amount the user typed is still the one they want.
  const staleQuote = useStaleQuoteRecovery(flow, quoteState, {
    hold: capturedPayment,
  })

  // A round whose quote died has one way forward, and a failure screen offering
  // a retry is a tap spent learning that. `retryAfterFailure` already sends this
  // case to the same place; taking it now spares the user the screen.
  // A captured payment is the exception: the card is already charged, so the
  // order call is replayed rather than repriced (a new price opens a second
  // sheet).
  const roundOutlivedItsQuote =
    flow.stepResponse !== undefined &&
    flow.phase === 'RETRYABLE' &&
    !capturedPayment &&
    isStaleQuoteFailure(flow.submitError)
  const giveUpOutlivedRound = useEffectEvent(() => {
    logger.info('connect-rn:fiat:round_quote_dead_requoting', {
      quoteRef: captureQuoteRefOf(flow),
      provider: flow.stepResponse?.provider,
    })
    // The dead price is cached and still inside its stated lifetime, so a
    // remount alone would show it again.
    quoteState.dropQuote()
    onBackToEntry()
  })
  useEffect(() => {
    if (roundOutlivedItsQuote) {
      giveUpOutlivedRound()
    }
  }, [roundOutlivedItsQuote])

  // Everything past the amount is a screen the user is part-way through — a
  // number, a code, the identity form — and this step is the stack's root,
  // which the sheet would otherwise let a swipe dismiss.
  // The screen stays up through the request it sends — the design's
  // "OTP – Submitting" is the contact screen with its button spinning, not the
  // amount screen for a round trip — and through a refusal, which it wears as
  // its notice. It steps aside once the flow has moved on, so it cannot show
  // again on a later visit to the entry.
  const contactRecovery = contactRefusal(quoteRefusal(flow.stepResponse))
  const phoneReverify = contactRecovery === 'reverify'
  const contactRateLimited = contactRecovery === 'rate-limited'
  const contactFailure =
    contactRound !== undefined && contactRecovery === 'change'
  const isContactStepOver =
    contactRateLimited ||
    (flow.page.screen !== 'AMOUNT_ENTRY' && !contactFailure && !phoneReverify)
  // A failure screen's own retry likewise stays on that screen while the
  // replay is in flight, rather than showing the amount for a round trip.
  const [isRetrying, setIsRetrying] = useState(false)
  useEffect(() => {
    if (!flow.isInFlight) {
      setIsRetrying(false)
    }
  }, [flow.isInFlight])
  // A form that failed in transit is sent again from its own Confirm, so the
  // replay-or-resend call the failure screen makes has to be made there too.
  const isReplayable = useReplayableFailure(flow)
  // Stripe mints its Link user on the device, so the account-creation form has
  // a client-side prerequisite no server-sent round error can report.
  const [stripeRegistrationError, setStripeRegistrationError] = useState<
    string | undefined
  >(undefined)
  // `registerLinkUser` is not idempotent: a second call for an email Stripe
  // already knows fails with "There is already a user ... in network LINK", so
  // two concurrent pushes turn a registration that worked into a visible
  // failure. Held in a ref rather than state because the guard has to be read
  // and set within one tap, before React has re-rendered anything.
  // The KYC round whose Stripe push is in flight or has already landed. Not a boolean: a landed
  // push must keep blocking *its own* round (repeating a registration can only fail) while leaving
  // the next one free — as a flag it latched on the first success and dead-ended every round after.
  const stripePushedRound = useRef<string | undefined>(undefined)
  const stripePushInFlight = useRef(false)
  // What earlier rounds of this session collected, so a later one does not ask
  // the buyer to retype it. Screen-local and never persisted: this is a
  // convenience for the person typing, not a cache the backend may rely on.
  // Scoped to the subject the answers belong to. These are one person's name,
  // address, tax identifier and date of birth; held unscoped, a host switching
  // users between rounds prefilled the new buyer's blank fields with the
  // previous one's and let them be submitted under a different assertion.
  const [carriedKyc, setCarriedKyc] = useState<{
    subject: string | undefined
    values: FormValues
  }>({ subject: userId, values: {} })
  const carriedKycValues =
    carriedKyc.subject === userId ? carriedKyc.values : {}
  const carryKycValues = useEffectEvent((values: FormValues) => {
    setCarriedKyc((held) =>
      held.subject === userId
        ? { subject: userId, values: { ...held.values, ...values } }
        : { subject: userId, values },
    )
  })
  const formError =
    flow.stepResponse?.state.kind === 'KYC' &&
    flow.stepResponse.state.phase === 'INPUT_REQUIRED'
      ? flow.stepResponse.state.error
      : undefined
  // Held rather than read off the response: the error the form opened on
  // answers the round before it, which the entry card already reported. Only
  // a response landing while the form is up is about what the buyer typed.
  const [roundError, setRoundError] = useState<FailureReason>()
  const wasOnForm = useRef(false)
  useEffect(() => {
    setRoundError(wasOnForm.current ? formError : undefined)
    wasOnForm.current = flow.page.screen === 'KYC_FORM'
  }, [flow.stepResponse])
  const kycNetworkFailure =
    flow.phase === 'RETRYABLE' &&
    flow.page.screen === 'KYC_FORM' &&
    !roundOutlivedItsQuote
  // A lapsed quote is carried on the form on purpose: the answers still count
  // and the price is replaced once the form completes, so it is not an outage.
  const kycProviderFailure =
    formError !== undefined &&
    formError.category !== 'USER_ERROR' &&
    !isExpiredQuoteMarker(formError)
  const announceKycFailure = useEffectEvent((reason: 'network' | 'provider') =>
    showKycUnavailableAlert(
      t,
      reason === 'network'
        ? {
            reason,
            ...(flow.page.screen === 'KYC_CAPTURE'
              ? { onRetry: retrySurface }
              : {}),
          }
        : { reason },
    ),
  )
  const rejectedState =
    flow.stepResponse?.state.kind === 'KYC' &&
    flow.stepResponse.state.phase === 'NO_ACTION_REQUIRED' &&
    flow.stepResponse.state.reason === 'REJECTED'
      ? flow.stepResponse.state
      : undefined
  const recoverableRejection =
    rejectedState?.failureReason?.category !== 'KYC_REJECTED' &&
    rejectedState?.failureReason?.retryable === true
  const announceRejection = useEffectEvent(() => {
    if (rejectedState === undefined) {
      return
    }
    const { category, code, recovery } = rejectedState.failureReason ?? {}
    const details = {
      category,
      code,
      recovery,
      provider: flow.stepResponse?.provider,
    }
    // No alert: the buyer reads the outcome in the banner on the screen they
    // were left on. Giving up the dead quote would take the banner with it,
    // so the routes out of a correctable rejection wait for the options card.
    if (recoverableRejection) {
      logger.info('connect-rn:fiat:kyc_rejection_recovery', details)
      logger.warn('connect-rn:fiat:kyc_rejected_recoverable', details)
      return
    }
    logger.info('connect-rn:fiat:kyc_rejected_restricted', details)
  })
  useEffect(() => {
    announceRejection()
  }, [rejectedState])
  const orderFailedAfterCapture = flow.phase === 'RETRYABLE' && capturedPayment
  const announceOrderFailedAfterCapture = useEffectEvent(() =>
    logger.warn('connect-rn:fiat:order_failed_after_capture', {
      provider: flow.stepResponse?.provider,
      quoteRef: shownQuote?.quoteRef,
      error:
        flow.submitError instanceof Error
          ? flow.submitError.message
          : String(flow.submitError),
    }),
  )
  useEffect(() => {
    if (orderFailedAfterCapture) {
      announceOrderFailedAfterCapture()
    }
  }, [orderFailedAfterCapture])
  const announcePaymentFailure = useEffectEvent(() =>
    showPaymentUnavailableAlert(
      t,
      fiat.paymentMethod === 'apple_pay',
      alertScheme,
    ),
  )
  useEffect(() => {
    if (kycNetworkFailure) {
      announceKycFailure('network')
    } else if (kycProviderFailure) {
      announceKycFailure('provider')
    }
  }, [kycNetworkFailure, kycProviderFailure, formError])
  const announceSurfaceFailure = useEffectEvent((failure: SurfaceFailure) => {
    const reason = surfaceFailureReason(failure.code)
    logger.warn('connect-rn:fiat:kyc_capture_failed', {
      code: failure.code,
      reason,
    })
    // A surface that never loaded gets another mount on dismissal. One that
    // loaded and gave up does not — but only on Stripe, whose repair cannot
    // succeed on a second attempt: `surfaceFailure` stays set and the buyer
    // lands on the terminal screen below. Every other rail mounts its own
    // document surface and publishes a fresh attempt, so terminating it here
    // would leave the buyer on a support-only alert over a surface that still
    // works, and with no terminal screen to land on.
    showKycUnavailableAlert(
      t,
      reason === 'network'
        ? { reason, onRetry: retrySurface }
        : // Support-only for Stripe, and only on the KYC capture. That is the
          // one surface whose repair cannot succeed on a second attempt, and it
          // is the only one with a terminal screen to land on. On any other
          // screen the alert has to stay retryable, or it dead-ends a buyer
          // over a surface that still works.
          flow.stepResponse?.provider === 'STRIPE' &&
            flow.page.screen === 'KYC_CAPTURE'
          ? { reason }
          : { reason: 'providerRetryable', onDismiss: retrySurface },
    )
  })
  const announceAuthFailure = useEffectEvent((failure: SurfaceFailure) =>
    logger.warn('connect-rn:fiat:auth_capture_failed', {
      provider: flow.stepResponse?.provider,
      code: failure.code,
      reason: failure.detail,
    }),
  )
  useEffect(() => {
    if (
      surfaceFailure !== undefined &&
      flow.page.screen === 'KYC_CAPTURE' &&
      surfaceFailure.code !== STRIPE_KYC_CANCELLED
    ) {
      announceSurfaceFailure(surfaceFailure)
    } else if (
      // A dismissed sheet routes like a failure — back to the quote, "Try again"
      // live — but it is not one, so it raises no alert. Telling a buyer the rail
      // is unavailable because they closed it is how a deliberate choice starts
      // looking like a broken integration.
      surfaceFailure !== undefined &&
      flow.page.screen === 'PAYMENT_CAPTURE' &&
      surfaceFailure.code !== STRIPE_PAYMENT_CANCELLED
    ) {
      announcePaymentFailure()
    } else if (
      surfaceFailure !== undefined &&
      flow.page.screen === 'AUTH_CAPTURE'
    ) {
      announceAuthFailure(surfaceFailure)
    }
  }, [surfaceFailure, flow.page.screen])

  useEffect(() => {
    if (isContactStepOver) {
      setContactRound(undefined)
    }
  }, [isContactStepOver])

  // A refusal the server could place on no field comes back on the quote, and
  // the amount screen has nowhere to show it: the screen the user was on just
  // closes. The design answers it with the platform's own alert over the
  // amount (2114:8131). Once per response — a re-render must not re-raise it.
  // Worded in the language current as it fires — through an effect event, so
  // a host changing locale re-words the alerts to come without re-raising the
  // one already up for this response.
  const announceRefusal = useEffectEvent((error: FailureReason) => {
    logger.warn('connect-rn:fiat:quote_refused', {
      code: error.code,
      category: error.category,
    })
    showQuoteRefusalAlert(error, t, alertScheme)
  })
  useEffect(() => {
    if (
      refusal !== undefined &&
      !flowQuoteExpired &&
      !contactFailure &&
      !phoneReverify &&
      !overLimitSilenced
    ) {
      announceRefusal(refusal)
    }
  }, [
    refusal,
    flowQuoteExpired,
    contactFailure,
    phoneReverify,
    overLimitSilenced,
  ])

  const close = () => {
    flow.exit()
    onCloseFlow()
  }

  // The identity SDK's token expires inside a document capture, and only the
  // screen knows the quote a replacement is minted against.
  const captureQuote =
    flow.stepResponse?.state.kind === 'KYC'
      ? flow.stepResponse.state.quote
      : undefined
  const captureQuoteRef = captureQuote?.quoteRef
  const renewCaptureToken = useCallback(async () => {
    if (captureQuoteRef === undefined) {
      return undefined
    }
    const renewed = await renewCaptureSurface(
      { apiKey, logger, quoteRef: captureQuoteRef },
      flow.identityAssertionProvider,
    )
    const token = renewed?.params.accessToken
    return typeof token === 'string' ? token : undefined
  }, [apiKey, captureQuoteRef, flow.identityAssertionProvider])

  // Stripe drives its own checkout loop and calls back once per attempt, so
  // this is handed to the surface rather than fired as a transition: the flow
  // does not advance between a 3DS challenge and the retry that follows it.
  const requestClientSecret = useCallback(
    ({
      callbackEndpoint,
      callbackIdField,
    }: {
      callbackEndpoint: string
      callbackIdField: string
    }) =>
      requestCheckoutClientSecret(
        { apiKey, logger, callbackEndpoint, callbackIdField },
        flow.identityAssertionProvider,
      ),
    [apiKey, flow.identityAssertionProvider],
  )

  // Provider-rendered auth and both capture states carry a surface an adapter
  // draws. The screen around them differs; the surface plumbing does not.
  const captureState = flow.stepResponse?.state
  const captureSurface =
    captureState?.kind === 'SESSION_AUTH' &&
    captureState.channel === 'PROVIDER_RENDERED'
      ? captureState.surface
      : (captureState?.kind === 'PAYMENT' || captureState?.kind === 'KYC') &&
          captureState.phase === 'CAPTURE'
        ? captureState.surface
        : undefined
  // Every `renderCaptureSurface` whose control came from `captureModel` must be handed this same
  // list. `captureControl` deciding a surface is claimed while the render tries a shorter list is
  // silent: the guard passes, no adapter claims it, `renderCaptureSurface` returns null, and the
  // screen is simply blank — no warning, because `capture_surface_unsupported` only fires when
  // `captureModel` itself is undefined. That mismatch has now caused the same blank screen on
  // AUTH_COMPONENT and on Stripe's Apple Pay PAY_SHEET.
  //
  // The authorize adapter belongs here too, not only in the AUTH_CAPTURE branch that renders it:
  // `captureModel` is what decides whether a surface is claimed at all, so leaving it out made an
  // AUTH_COMPONENT surface look unsupported and dead-ended the screen before anything rendered.
  const stripeCaptureAdapters =
    stripeOnramp === undefined
      ? []
      : [
          stripeKycAdapter(stripeOnramp, flow.stepResponse ?? {}),
          stripePaymentAdapter(stripeOnramp, flow.stepResponse ?? {}),
          stripeCheckoutAdapter(stripeOnramp, requestClientSecret),
          stripeAuthorizeAdapter(stripeOnramp, flow.stepResponse ?? {}),
        ]
  const captureModel =
    captureSurface === undefined
      ? undefined
      : captureControl(captureSurface, stripeCaptureAdapters)
  const authResultTransition =
    flow.page.screen === 'AUTH_CAPTURE'
      ? flow.ctas.find(
          (transition) => transition.endpoint === 'POST /fiat/session/verify',
        )
      : undefined
  const isEmbeddedAuth =
    flow.page.screen === 'AUTH_CAPTURE' &&
    captureModel === 'EMBEDDED' &&
    captureSurface !== undefined &&
    authResultTransition !== undefined

  const isAmountHosted =
    paymentUnavailable ||
    flow.page.screen === 'AMOUNT_ENTRY' ||
    flow.page.screen === 'QUOTE_REVIEW' ||
    isEmbeddedAuth ||
    (flow.page.screen === 'PAYMENT_CAPTURE' && captureModel === 'EMBEDDED')
  useLockFlowDismiss(contactRound !== undefined || !isAmountHosted)

  // A surface no adapter claims dead-ends the deposit, and the notice it lands
  // on cannot say which provider was refused.
  useEffect(() => {
    if (captureSurface === undefined || captureModel !== undefined) {
      return
    }
    logger.warn('connect-rn:fiat:capture_surface_unsupported', {
      kind: captureSurface.kind,
      provider: captureSurface.provider,
    })
  }, [captureSurface, captureModel])

  // Every screen this build cannot draw shows the same sentence, so without
  // this the console says nothing about which state produced it. Keyed on the
  // page so a re-render does not repeat it.
  const unsupportedScreen = UNDRAWN_SCREENS.has(flow.page.screen)
    ? flow.page.screen
    : undefined
  const pageIdentity = flow.page.identity
  const unsupportedState = flow.stepResponse?.state
  useEffect(() => {
    if (unsupportedScreen === undefined) {
      return
    }
    logger.warn('connect-rn:fiat:screen_unsupported', {
      screen: unsupportedScreen,
      identity: pageIdentity,
      state: unsupportedState?.kind,
      phase:
        unsupportedState !== undefined && 'phase' in unsupportedState
          ? unsupportedState.phase
          : undefined,
      surface:
        unsupportedState !== undefined && 'surface' in unsupportedState
          ? unsupportedState.surface?.kind
          : undefined,
      provider: flow.stepResponse?.provider,
    })
    // The state rides on the page, so the identity is what changes with it.
  }, [unsupportedScreen, pageIdentity])

  // Once the flow holds a round it *is* the screen. The quote query keeps its
  // last answer, which is the step before this one — firing it walks the user
  // back through a challenge they already passed.
  const flowStep = flow.stepResponse
  const provider = flowStep?.provider ?? quoteState.provider
  const flowCta = flowStep === undefined ? undefined : selectEntryCta(flowStep)
  const entryCta = flowCta ?? quoteState.entryCta
  const isEntryReady =
    !paymentUnavailable &&
    (flowCta === undefined ? quoteState.isQuoteFresh : shouldRenderCta(flowCta))
  const shownQuote = flow.quote ?? quoteState.quote

  const identityDetour = isEntryReady && isIdentityDetour(entryCta)
  const payableContext = JSON.stringify([
    userId,
    provider,
    fiat.paymentMethod,
    fiat.cryptoCurrencyCode,
    fiat.chainId,
    quoteCurrency,
  ])
  const [lastPayableContext, setLastPayableContext] = useState<string>()
  useEffect(() => {
    if (isEntryReady && entryCta.kind === 'PAY') {
      setLastPayableContext(payableContext)
    }
  }, [isEntryReady, entryCta.kind, payableContext])
  // A first-use requirement is not evidence that the buyer crossed a limit.
  const isKycThreshold =
    identityDetour &&
    (refusal?.code === 'LIMITS_EXCEEDED_UPGRADEABLE' ||
      lastPayableContext === payableContext)
  useEffect(() => {
    if (!identityDetour) {
      return
    }
    logger.info('connect-rn:fiat:identity_entry', {
      presentation: isKycThreshold ? 'threshold' : 'first_use',
      provider,
      paymentMethod: fiat.paymentMethod,
      code: refusal?.code,
    })
  }, [
    identityDetour,
    isKycThreshold,
    provider,
    fiat.paymentMethod,
    refusal?.code,
    logger,
  ])

  // The footer discloses what the normal action accepts before the tap.
  const terms = isEntryReady ? termsToAccept(entryCta) : undefined
  const rebuildPhoneRound = useEffectEvent(() => {
    const fields = sessionContactFields(entryCta)
    const round = contactRoundToAsk(fields, entryCta, terms)
    logger.info('connect-rn:fiat:contact_reverify', {
      code: 'PHONE_REVERIFY',
      fields: fields.map((field) => field.name),
    })
    if (round === undefined) {
      logger.warn('connect-rn:fiat:contact_reverify_missing_transition', {
        code: 'PHONE_REVERIFY',
      })
      return
    }
    // The replacement quote owns the new session, including its quoteRef.
    for (const field of fields) {
      const value = field.kind === 'PHONE' ? contacts.phone : contacts.email
      if (value !== undefined) {
        flow.setField(field.name, value)
      }
    }
    setContactRound(round)
  })
  useEffect(() => {
    if (phoneReverify) {
      rebuildPhoneRound()
    }
  }, [phoneReverify, flowStep])

  /** Fires whichever round owns the screen, under its own quoteRef. */
  const fireEntry = () => {
    const pinned = firing.current
    firing.current = undefined
    if (pinned !== undefined) {
      void flow.submit(pinned)
      return
    }
    if (flowCta === undefined) {
      quoteState.submitQuote()
      return
    }
    const transition = submittableTransition(flowCta)
    if (transition !== undefined) {
      void flow.submit(transition)
    }
  }

  /**
   * The round a tap committed to, held across the render an acceptance costs.
   * Rediscovering it after that render can find a refetching query and send
   * nothing, which reads as a dead button.
   */
  const submitEntry = (round?: ContactRound) => {
    firing.current = round?.transition
    const accept = round === undefined ? terms : round.terms
    if (accept === undefined) {
      fireEntry()
      return
    }
    // Never defaulted: the value asserts a moment the user acted, and the
    // server refuses anything that is not one.
    setAcceptedNotice(accept)
    flow.setField(accept.name, acceptedTerms(accept, new Date()))
    setIsAccepting(true)
  }

  useEffect(() => {
    if (!isAccepting) {
      return
    }
    setIsAccepting(false)
    fireEntry()
    // The acceptance is what this waits for; the sender is read at that point.
  }, [isAccepting])

  // The round was priced for the amount that bought it, and no re-quote runs
  // underneath it. Editing gives it up rather than showing a figure nobody
  // quoted.
  const changeAmount = (next: string) => {
    if (flowStep !== undefined || flow.isInFlight) {
      logger.info('connect-rn:fiat:checkout_abandoned', {
        reason: flow.isInFlight ? 'submit_in_flight' : 'round_entered',
        quoteRef: shownQuote?.quoteRef,
      })
      quoteState.dropQuote()
      onBackToEntry()
    }
    onChangeAmount(next)
  }

  // The verdict travels with the amount screen now: the flow no longer leaves
  // it for a full-screen outcome, so the banner and the button carry the news.
  // The check resolves on whichever response carries it: the round the flow
  // entered, or — when the buyer comes back and asks for more than their
  // unchanged limit — the quote answered with the check instead of a price.
  const kycResolution = selectKycOutcome(resolvedStep?.state)
  // A round still holding its form is correctable by filling it in, not by
  // waiting: there is no poll on it, and the screen keeps the verdict up
  // until the buyer asks for the form.
  const awaitsForm =
    resolvedStep?.state.kind === 'KYC' &&
    resolvedStep.state.phase === 'INPUT_REQUIRED'
  const [resolvedKyc, setResolvedKyc] = useState<
    KycOutcome | 'complete' | undefined
  >(undefined)
  const isOrdered =
    flow.page.screen === 'ORDER_REVIEW' ||
    flow.page.screen === 'ORDER_PENDING' ||
    flow.page.screen === 'ORDER_TRACKING' ||
    flow.page.screen === 'SUCCESS'
  // A check answered in place of a price still has to reach the flow, or its
  // poll never runs and a review never resolves. Nothing else enters that
  // round now that the action opens the options card instead of submitting.
  const enteredCheck = useRef(false)
  const enterResolvedCheck = useEffectEvent(() => {
    enteredCheck.current = true
    logger.info('connect-rn:fiat:kyc_check_entered', {
      outcome: kycResolution?.outcome,
      provider,
    })
    quoteState.submitQuote()
  })
  useEffect(() => {
    // The round is in: a later one is free to enter on its own account.
    if (flow.stepResponse !== undefined) {
      enteredCheck.current = false
      return
    }
    // Otherwise once only. A submit that leaves no step response behind would
    // otherwise be fired again by the next poll that moves the outcome.
    if (kycResolution === undefined || awaitsForm || enteredCheck.current) {
      return
    }
    enterResolvedCheck()
  }, [kycResolution?.outcome, awaitsForm, flow.stepResponse])

  // The verdict outlives the response that carried it: a correctable
  // rejection gives up its quote to buy a fresh one, and the banner has to
  // survive that rather than blink out with the state.
  useEffect(() => {
    const resolved = kycResolution?.outcome
    if (resolved !== undefined) {
      setResolvedKyc(resolved)
      return
    }
    // No single response says the check just passed. What says it is a wait
    // that ends with an entry the buyer can pay from again.
    setResolvedKyc((shown) => (shown === 'pending' ? 'complete' : shown))
  }, [kycResolution?.outcome])
  useEffect(() => {
    if (isOrdered) {
      setResolvedKyc(undefined)
    }
  }, [isOrdered])

  // The provider's own page sees its transfer before our poll does. On the
  // direct route that transfer IS the delivery, so its word ends the deposit
  // — once the order it belongs to exists. Their event can land while the
  // payment report is still in flight, and the host is owed that order's id,
  // so the word is held until the report answers.
  const orderCreated = flow.stepResponse?.state.kind === 'ORDER'
  const [providerSettled, setProviderSettled] = useState(false)
  const markProviderSettled = () => {
    logger.info('connect-rn:fiat:provider_settled', {
      orderId: orderIdOf(flow),
      provider,
      awaitingOrder: !orderCreated,
      route: isDirect ? 'recipient' : 'depositAddress',
    })
    setProviderSettled(true)
  }
  // Their event is the fast path, not the only one: Coinbase has renamed it
  // before, and a spelling this build predates would leave the flow waiting.
  // The poll still ends where it always did, and settles the same deposit.
  const queryClient = useQueryClient()
  const deliveryConfirmed =
    flow.page.screen === 'SUCCESS' || (providerSettled && orderCreated)

  const settled = useRef(false)
  const settleDeposit = useEffectEvent(() => {
    if (settled.current) {
      return
    }
    settled.current = true
    // The purchase spent part of what this buyer had left, so what the session
    // learned about the route is now too generous. Dropping it is cheaper than
    // doing the provider's arithmetic here and being subtly wrong about fees,
    // windows and pending orders (ENG-6302).
    forgetRouteLimits(queryClient)
    const orderId = orderIdOf(flow)
    const method = depositMethodType(fiat.paymentMethod)
    logger.info('connect-rn:fiat:deposit_settled', {
      orderId,
      provider,
      method,
      route: isDirect ? 'recipient' : 'depositAddress',
    })
    // Whatever is claimed below, the deposit went through: leaving now is not
    // a cancellation.
    outcome.ordered()
    // An order the backend still has to sweep has not arrived, so nothing is
    // claimed for it.
    if (!isDirect) {
      return
    }
    showDepositToast({
      title: t('fiat.depositReceived', {
        amount:
          paidAmount(flow, shownQuote) ??
          formatFiat(
            Number(amount),
            shownQuote?.fiat.currency ?? DEFAULT_FIAT_CURRENCY,
          ),
      }),
      detail: t('fiat.depositReceivedFrom', {
        method: config.methodName ?? methodDisplayName(fiat.paymentMethod, t),
      }),
    })
    // Closed first: the callback's contract is that the flow is gone, and a
    // host that opens the next flow from it must not have it closed under it.
    close()
    if (orderId === undefined) {
      logger.warn('connect-rn:fiat:order_id_missing', { provider })
      return
    }
    if (method !== undefined) {
      outcome.submitted(orderId, method)
    }
  })
  useEffect(() => {
    if (deliveryConfirmed) {
      settleDeposit()
    }
  }, [deliveryConfirmed])
  // A quote answered with the check carries no limits of its own, and the
  // whole point of that answer is that the limit has not changed — so the
  // last one quoted is the one to offer. Unknown until a price has been seen,
  // which is why the row can be absent.
  //
  // Held with the currency it was quoted in, and as a number: the server's
  // string reaches both the row's copy and the amount field, and a figure
  // from one corridor under another's symbol names a ceiling nobody offered.
  const [knownCeiling, setKnownCeiling] = useState<{
    max: number
    currency: string
  }>()
  const quotedCurrency = quoteState.quote?.fiat.currency
  // The server states one ceiling: a priced quote intersects the corridor with
  // this buyer's own allowance, and a refusal narrows it to the cap that
  // refused. So there is one number to read, and it is always theirs.
  const quotedCeiling = quoteState.limits?.max
  useEffect(() => {
    const max = Number(quotedCeiling)
    if (quotedCurrency === undefined || !Number.isFinite(max) || max <= 0) {
      return
    }
    setKnownCeiling({ max, currency: quotedCurrency })
  }, [quotedCeiling, quotedCurrency])

  // A check answered in place of a price still names the currency it was
  // asked in, which is the one the ceiling is quoted in.
  const limitCurrency =
    shownQuote?.fiat.currency ??
    (resolvedStep?.state.kind === 'KYC'
      ? resolvedStep.state.quote?.fiat.currency
      : undefined)

  // ENG-6302. Read during the render that carries the refusal, never folded in by
  // an effect: an effect leaves one frame with the refusal landed and no ceiling,
  // and that is the frame the action slot draws its grey "Try again" in.
  const refusedMax = Number(quoteState.maximum?.amount)
  const refusedCeiling =
    quoteState.maximum !== undefined &&
    Number.isFinite(refusedMax) &&
    refusedMax > 0
      ? { max: refusedMax, currency: quoteState.maximum.currency }
      : undefined

  // The other refusal channel: a re-presented QUOTE lands on `flow.stepResponse`,
  // which never passes through the query `useFiatQuote` reads. Unread here, the cap
  // the server just named is on the wire with nothing on screen able to see it.
  // Only a refused one: an ordinary re-presentation after a phone/OTP round carries
  // the committed provider's own envelope, and letting that outrank `knownCeiling`
  // would name a maximum below what the route serves with nothing refused.
  const flowQuote =
    flow.stepResponse?.state.kind === 'QUOTE' &&
    flow.stepResponse.state.error !== undefined
      ? flow.stepResponse.state
      : undefined
  const flowMax = Number(flowQuote?.limits?.max)
  const flowCurrency = flowQuote?.quote?.fiat.currency
  const flowCeiling =
    flowCurrency !== undefined && Number.isFinite(flowMax) && flowMax > 0
      ? { max: flowMax, currency: flowCurrency }
      : undefined

  // One figure, and only while it still belongs to the currency being quoted: a
  // refusal carries its own, and is the only answer there is when no price came back.
  const ceiling =
    refusedCeiling ??
    flowCeiling ??
    (knownCeiling?.currency === limitCurrency ? knownCeiling : undefined)
  // The tightest that applies: each is a real refusal, so the lowest is the one the
  // buyer meets first, and they are never shown a number the server would not give.
  const limitUsd =
    ceiling !== undefined &&
    (clientMaxUsd === undefined || ceiling.max < Number(clientMaxUsd))
      ? String(ceiling.max)
      : clientMaxUsd

  const depositMax =
    ceiling === undefined || limitUsd === undefined
      ? undefined
      : formatFiat(Number(limitUsd), ceiling.currency)

  const kycCompletion = useKycCompletion(flow)

  // A step-up raises the tier, which is the one direction the record never
  // moves on its own — it only narrows. Left in place it would hold the buyer
  // under the ceiling they just cleared.
  //
  // Keyed on leaving KYC at all, not on any one of its screens. A step-up can
  // be a form, a document capture (`KYC/CAPTURE`, which a quote can return
  // directly and which never shows the form) or a review that resolves, and
  // `useKycCompletion` is deliberately silent about a pass — a form answered
  // with a price is not a verdict it has a screen for. Every narrower signal
  // misses a way the tier can rise, and missing one holds the buyer under the
  // old ceiling for the rest of the session.
  const inKyc = flow.stepResponse?.state.kind === 'KYC'
  const wasInKyc = useRef(false)
  useEffect(() => {
    if (inKyc) {
      wasInKyc.current = true
      return
    }
    if (!wasInKyc.current) {
      return
    }
    wasInKyc.current = false
    forgetRouteLimits(queryClient)
  }, [inKyc, queryClient])

  // A server-supplied label belongs to the round that carried it: while a
  // re-quote is due, the button says the default thing rather than the stale
  // one.
  const railCopy = resolveDepositRailCopy(config, fiat.paymentMethod, t)
  const quotedLabel = isEntryReady
    ? entryCtaLabel(entryCta, t, railCopy.submitLabel)
    : railCopy.submitLabel
  // Only asked for when the step asks: a transition declaring no input never
  // shows the screen.
  const contactFields = isEntryReady ? sessionContactFields(entryCta) : []

  /**
   * Starts whatever the entry CTA holds, asking for the contacts it declares
   * first. A round already in hand is entered rather than sent.
   */
  const openEntryRound = () => {
    const round = contactRoundToAsk(contactFields, entryCta, terms)
    if (round === undefined) {
      submitEntry()
      return
    }
    setContactRound(round)
  }

  // The routes out of a held deposit, in the order the frames list them.
  // "Buy tokens directly" is descoped from launch (ENG-6095), so it is drawn
  // nowhere; a re-do is offered only while the flow still publishes the
  // transition that restarts the check.
  //
  // Read off whichever response carried the check — the entered round, or the
  // quote that answered with it — not the entry CTA, which speaks for a price.
  const kycRedo = resolvedStep?.transitions.find(
    (transition): transition is SubmitTransition =>
      transition.mode === 'SUBMIT',
  )
  const redoRoute = kycRedoRoute({
    awaitsForm,
    openEntryRound,
    transition: kycRedo,
    flow,
  })
  const kycRoutes = kycOptionRows({
    outcome: resolvedKyc,
    depositMax,
    t,
    onRedo:
      redoRoute === undefined
        ? undefined
        : () => {
            setOnKycOptions(false)
            logger.info('connect-rn:fiat:kyc_route_taken', {
              route: 'redo',
              outcome: resolvedKyc,
              transition: awaitsForm ? undefined : kycRedo?.id,
              entersRound: awaitsForm,
            })
            redoRoute()
          },
    onDepositMax: () => {
      setOnKycOptions(false)
      logger.info('connect-rn:fiat:kyc_route_taken', {
        route: 'deposit_max',
        outcome: resolvedKyc,
        currency: ceiling?.currency,
      })
      // Through `changeAmount`, so the held round is given up and the new
      // amount is priced — setting the input alone leaves quoting off and
      // the next press back on the card.
      //
      // The effective cap, not the tier's raw ceiling: offering a figure the
      // screen would then refuse makes the row a dead end.
      if (limitUsd !== undefined) {
        changeAmount(limitUsd)
      }
    },
    onSupport: () => {
      logger.info('connect-rn:fiat:kyc_route_taken', {
        route: 'support',
        outcome: resolvedKyc,
      })
      openKycSupport()
    },
    onClose: () => {
      logger.info('connect-rn:fiat:kyc_route_taken', {
        route: 'close',
        outcome: resolvedKyc,
      })
      close()
    },
  })

  // A check the backend asks for before the first deposit resolves to
  // nothing yet, so it reports itself rather than borrowing an outcome.
  const identityNoticeState: KycNoticeState | undefined =
    resolvedKyc ?? (identityDetour && !isKycThreshold ? 'required' : undefined)

  const submitLabel = kycOutcomeCta(identityNoticeState, t) ?? quotedLabel

  // The value is still collected, but the keyboard and the copy are guesses
  // once the name is one we have not served — say so rather than let a wrong
  // screen look deliberate.
  const unfamiliarContactNames = contactFields
    .map((field) => field.name)
    .filter(isUnfamiliarContactName)
    .join(',')
  useEffect(() => {
    if (unfamiliarContactNames === '') {
      return
    }
    logger.warn('connect-rn:fiat:session_contact_unfamiliar', {
      input: unfamiliarContactNames,
    })
  }, [logger, unfamiliarContactNames])

  // Assertion-backed purchases resolve their destination on the backend.
  // Legacy purchases wait for a local address; verification rounds need none.
  // `shouldRenderCta` keeps a PENDING quote from enabling a button whose tap
  // does nothing.
  const isRoundEntry = enteredStep(entryCta) !== undefined
  const canSubmitQuote =
    isEntryReady &&
    !flow.isInFlight &&
    (isRoundEntry ||
      identityAssertion !== undefined ||
      depositAddress !== undefined) &&
    shouldRenderCta(entryCta)

  // The address lookup owns its own failure: the quote is fine, so nothing
  // else on the screen would ever say why the action cannot be taken.
  const depositAddressUnavailable =
    depositAddressError !== undefined &&
    !isRoundEntry &&
    depositAddress === undefined
  useEffect(() => {
    if (!depositAddressUnavailable) {
      return
    }
    logger.warn('connect-rn:fiat:deposit_address_unavailable', {
      chainId: fiat.chainId,
      error: depositAddressError?.name,
    })
  }, [depositAddressUnavailable, depositAddressError?.name, fiat.chainId])

  // Once the order is made, an embedded provider button (Coinbase's Apple Pay
  // page is nothing but one) takes the action's slot — "Deposit with Apple
  // Pay" is the amount screen's own action in the design, not a screen.
  const isEmbeddedCapture =
    flow.page.screen === 'PAYMENT_CAPTURE' &&
    captureModel === 'EMBEDDED' &&
    captureSurface !== undefined

  // The provider's button has two sources — a capture state, or a quote that
  // carried its own control. Coinbase's order arrives already made, so its
  // page rides on the quote; Transak's comes from the capture. `surfaceKey`
  // differs between them, and reusing the wrong one remounts the page.
  const quoteControl = surfaceControl(entryCta)
  const liveSlot = embeddedSlot({
    captureSurface: isEmbeddedCapture ? captureSurface : undefined,
    quoteControl,
    shownQuoteRef: shownQuote?.quoteRef,
    surfaceAttempt,
  })

  // The order response takes that control away, and the page is what reports
  // the transfer — it reports it after the order exists. Held with the key it
  // was drawn under, so the news is not unmounted before it arrives.
  const [heldSlot, setHeldSlot] = useState<EmbeddedSlot>()
  useEffect(() => {
    if (liveSlot !== undefined) {
      setHeldSlot(liveSlot)
    }
  }, [liveSlot?.key])

  // "ApplePay Awaiting": everything between the sheet closing and the order's
  // outcome is spent here — keypad ghosted, the action waiting in its slot.
  // The phase ends it early: a capture handed back with a surface of its own
  // is running again, so the button returns.
  const isAwaitingSettlement =
    capturedPayment &&
    flow.phase !== 'SURFACE_RUNNING' &&
    WAITS_ON_PAYMENT.has(flow.page.screen)
  // The paid order failed: the wait is over and the buyer gets the amount
  // screen back — keypad live, the server's recovery in the slot, the failure
  // said over the header rather than on a screen of its own.
  const orderFailed = capturedPayment && flow.page.screen === 'FAILURE'
  let renderSubmit = createFiatSubmitRenderer({
    amount,
    entryCta,
    isEntryReady,
    flowRefused: refusal !== undefined,
    paymentUnavailable,
    onRetryFlowRefusal: onBackToEntry,
    onRetryUnavailable: () => {
      logger.info('connect-rn:fiat:blocked_retry', {
        hasEnteredFlow: flowStep !== undefined,
      })
      if (flowStep !== undefined) {
        onBackToEntry()
        return
      }
      quoteState.retryQuote()
    },
    shownQuoteRef: shownQuote?.quoteRef,
    quoteState,
    flow,
    reportSurface,
    t,
    canSubmitQuote,
    depositAddressUnavailable,
    onRetryDepositAddress: retryDepositAddress,
    surfaceAttempt,
    onSurfaceFailed: failPaymentSurface,
    onSurfaceReadyChange: setSurfaceReady,
    onSettled: markProviderSettled,
    onClose: close,
  })

  if (isEmbeddedCapture) {
    const paymentSurface = captureSurface
    renderSubmit = () => (
      <SurfaceSlot
        surfaceKey={`capture:${surfaceAttempt}`}
        label={t('common.loading')}
        size={SURFACE_MATCHED_SIZE}
        onReadyChange={setSurfaceReady}
        renderSurface={(onReady) =>
          renderCaptureSurface({
            surface: paymentSurface,
            adapters: stripeCaptureAdapters,
            placement: 'INLINE',
            onReady,
            onFailed: failPaymentSurface,
            onClose: close,
            onSettled: markProviderSettled,
            report: (result) => void reportSurface(result),
          })
        }
      />
    )
  }

  if (isEmbeddedAuth) {
    const authSurface = captureSurface
    const transition = authResultTransition
    renderSubmit = () => (
      <SurfaceSlot
        surfaceKey={`auth:${flow.page.identity}:${surfaceAttempt}`}
        label={t('common.loading')}
        size={SURFACE_MATCHED_SIZE}
        readyCeilingMs={30_000}
        onReadyTimeout={() => failSurface('surface_ready_timeout')}
        onReadyChange={setSurfaceReady}
        renderSurface={(onReady) =>
          renderCaptureSurface({
            surface: authSurface,
            adapters: stripeCaptureAdapters,
            placement: 'INLINE',
            onReady,
            onFailed: failSurface,
            onClose: close,
            report: (result) => {
              void flow.submit(transition, result)
            },
          })
        }
      />
    )
  }

  // Covered rather than replaced, and under the key it was already drawn
  // with: unmounting the provider's page — or remounting it under a new key —
  // would drop the transfer report this route waits on. A rail that drew no
  // page of its own still waits, so the bare slot stands in for it.
  if (isAwaitingSettlement && heldSlot === undefined) {
    renderSubmit = () => (
      <SurfaceSlot label={t('common.loading')} size={SURFACE_MATCHED_SIZE} />
    )
  }
  if (isAwaitingSettlement && heldSlot !== undefined) {
    const slot = heldSlot
    renderSubmit = () => (
      <SurfaceSlot
        surfaceKey={slot.key}
        label={t('common.loading')}
        size={SURFACE_MATCHED_SIZE}
        covered
        onReadyChange={setSurfaceReady}
        renderSurface={(onReady) =>
          renderCaptureSurface({
            surface: slot.surface,
            placement: 'INLINE',
            onReady,
            onFailed: failPaymentSurface,
            onClose: close,
            onSettled: markProviderSettled,
            report: (result) => void reportSurface(result, slot.transition),
          })
        }
      />
    )
  }

  if (orderFailed) {
    const recovery = recoveryCta(flow, t)
    if (recovery !== undefined) {
      renderSubmit = () => (
        <FiatActionButton label={recovery.label} onPress={recovery.onPress} />
      )
    }
  }

  if (
    (surfaceFailure !== undefined && flow.page.screen === 'PAYMENT_CAPTURE') ||
    surfaceFailure?.code === STRIPE_KYC_CANCELLED
  ) {
    renderSubmit = () => (
      <FiatActionButton label={t('common.tryAgain')} onPress={retrySurface} />
    )
  }

  const preparesCheckout = useFiatCheckoutPreparation({
    provider,
    paymentMethod: fiat.paymentMethod,
    entryCta,
    quoteRef: shownQuote?.quoteRef,
    ready:
      canSubmitQuote && contactFields.length === 0 && isQuotableAmount(amount),
    prepare: submitEntry,
  })

  // Stripe hands a declined round straight back as SESSION_AUTH with `start` offered again, and the
  // controller unmounts while Stripe's sheet owns the screen — so its own mount guard cannot tell a
  // fresh arrival from a return. Remembered on the step, which outlives both, the dismissal turns
  // the remount into an offer to continue instead of reopening the sheet.
  const [stripeAuthDeclined, setStripeAuthDeclined] = useState(false)

  // Stripe's session-auth round draws nothing of its own: its controller posts
  // `/fiat/session` on mount and the buyer has nothing to answer. Handing it a
  // screen would replace the one they just acted on with a bare spinner, so the
  // entry screen stays put and pends its action instead.
  const sessionAuthState =
    flowStep?.state.kind === 'SESSION_AUTH' ? flowStep.state : undefined
  const isStripeSessionAuthPending =
    provider === 'STRIPE' &&
    flow.page.screen === 'OTP' &&
    sessionAuthState?.channel === 'PROVIDER_RENDERED'

  // Coinbase and MoonPay need a server-side payment session before their
  // embedded Apple Pay button can be drawn, so the slot spins while the quote
  // prepares that non-charging session.
  const isPreparingCheckout =
    (preparesCheckout && (canSubmitQuote || flow.isInFlight)) ||
    isStripeSessionAuthPending
  // The fee sits beside the action and reads as part of it, so it holds its
  // pending state for as long as the action slot is still loading: the quote,
  // the checkout it prepares, then the provider button's own warm-up.
  const isActionPending =
    (!isEntryReady && refusal === undefined) ||
    !isSurfaceReady ||
    isPreparingCheckout

  const notice = isKycThreshold ? (
    <FiatKycNotice title="" detail={t('fiat.kycLimitNotice')} />
  ) : undefined

  // The provider's own page owns the action on this screen, so a failure it
  // reports has nowhere else to land. It rides over the header rather than in
  // the notice slot, which would push the amount down under the buyer's eye.
  let failureCopy: FiatFailureBannerProps | undefined
  if (orderFailed) {
    failureCopy = { title: t('fiat.failure'), detail: t('fiat.submitFailed') }
  } else if (
    surfaceFailure !== undefined &&
    flow.page.screen === 'PAYMENT_CAPTURE' &&
    // A buyer who dismissed the Apple Pay sheet was refused nothing. Banner-ing
    // their own choice as a payment failure is how a deliberate back-out starts
    // reading as a broken rail. "Try again" below still stands.
    surfaceFailure.code !== STRIPE_PAYMENT_CANCELLED
  ) {
    failureCopy = {
      title: t('fiat.paymentFailed'),
      detail: surfaceFailure.detail ?? t('fiat.submitFailed'),
    }
  }
  const failureBanner =
    failureCopy === undefined ? undefined : (
      <View style={[styles.failureBanner, { top: bannerTop }]}>
        <FiatFailureBanner {...failureCopy} />
      </View>
    )

  // The resolved banner is a toast over the header, not the screen's inline
  // notice slot: it covers the brand row rather than pushing the amount down.
  const outcomeBanner =
    identityNoticeState === undefined ? undefined : (
      <View style={styles.outcomeBanner} pointerEvents="none">
        <FiatKycNotice
          status={identityNoticeState}
          {...kycOutcomeCopy(
            identityNoticeState,
            t,
            kycHoldRetryDetail(flow, t),
          )}
        />
      </View>
    )

  // Once accepted, the disclosure stands while the flow is past the entry on
  // the round it was accepted for: an embedded provider button is still that
  // tap's "continuing". Back on a quote, that quote's own CTA says what its
  // tap accepts, so a round handed back without terms shows none — except on
  // Coinbase, which requires its disclosure on every screen that prices.
  const isPastEntry =
    flowStep !== undefined &&
    flow.page.screen !== 'AMOUNT_ENTRY' &&
    flow.page.screen !== 'QUOTE_REVIEW'
  const disclosedTerms =
    terms ??
    (isPastEntry ? acceptedNotice : undefined) ??
    (provider === 'COINBASE' ? COINBASE_STANDING_TERMS : undefined)
  const termsNotice =
    disclosedTerms === undefined ? undefined : (
      <FiatTermsNotice terms={disclosedTerms} />
    )

  // Freeze the disclosure at the moment its fee line is pressed. A quote can
  // refresh while the drawer is open, but that must not rewrite the figures or
  // rate the user asked to inspect. The next open takes the latest quote.
  const openFeeDetails = () => {
    setFeeRows(quoteFeeRows(shownQuote, t))
    setFeeSummary(quoteFeeSummary(shownQuote, railCopy.methodName, t))
    presentFeeDetails()
  }
  const entryScreen = (
    <Fragment key="amount-entry">
      <DepositAmountScreen
        title={config.title ?? t('depositAmount.title')}
        brandIcon={config.brandIcon}
        accountContext={accountBalanceChipProps(config.accountContext, locale)}
        input={amount}
        onChangeInput={changeAmount}
        quickAmounts={(config.quickAmountsUsd ?? DEFAULT_QUICK_AMOUNTS).map(
          (value) => ({ value, label: `$${groupThousands(value)}` }),
        )}
        limitUsd={limitUsd}
        minLimitUsd={config.minLimitUsd}
        disabled={lockAmount || isAwaitingSettlement}
        fee={quoteFee(shownQuote, amount, t)}
        feePending={isActionPending}
        onPressFee={openFeeDetails}
        feeAccessibilityLabel={t('feeDetails.title')}
        methodBadge={railCopy.methodBadge}
        notice={notice}
        disclosure={termsNotice}
        submitLabel={submitLabel}
        // A resolved check makes the action a way forward, not a payment, so
        // it is live on the routes it opens rather than on a fresh price.
        canSubmit={canSubmitQuote || kycRoutes !== undefined}
        onSubmit={() => {
          // A resolved check turns the action into a way forward, not a
          // payment: the card carries the routes out.
          if (kycRoutes !== undefined) {
            logger.info('connect-rn:fiat:kyc_options_opened', {
              outcome: resolvedKyc,
              routes: kycRoutes.map((row) => row.title),
              hasCeiling: ceiling !== undefined,
            })
            setOnKycOptions(true)
            return
          }
          openEntryRound()
        }}
        renderSubmit={
          isPreparingCheckout
            ? () => (
                <SurfaceSlot
                  label={t('common.loading')}
                  size={SURFACE_MATCHED_SIZE}
                />
              )
            : renderSubmit
        }
        amountAccessibilityLabel={t('depositAmount.amountLabel')}
        backspaceAccessibilityLabel={t('depositAmount.delete')}
        showAttribution={!hideAttribution}
      />

      {/* After the screen, not before it: an absolute sibling drawn first
          sits under the header it is meant to cover. */}
      {failureBanner ?? outcomeBanner}

      <FeeDetailsSheet
        sheet={sheet}
        rows={feeRows}
        {...(feeSummary === undefined ? {} : { summary: feeSummary })}
      />
    </Fragment>
  )

  if (onKycOptions && kycRoutes !== undefined) {
    return (
      <FiatKycOptionsScreen
        glyph={resolvedKyc === 'pending' ? 'pending' : 'soft'}
        title={kycOptionsTitle(resolvedKyc, t)}
        optionsLabel={t('fiat.kycOptionsLabel')}
        options={kycRoutes}
        onBack={() => setOnKycOptions(false)}
      />
    )
  }

  if (accountIneligible) {
    return (
      <FiatStatusScreen
        tone="failure"
        title={t('fiat.accountIneligibleTitle')}
        detail={t('fiat.accountIneligibleDetail')}
        onClose={close}
      />
    )
  }

  if (recoverableRejection || paymentUnavailable) {
    return entryScreen
  }

  if (surfaceFailure !== undefined && flow.page.screen === 'PAYMENT_CAPTURE') {
    return entryScreen
  }

  // A dismissed KYC sheet, answered exactly as a dismissed pay sheet is. The
  // terminal below is for a rail that refused; a buyer who closed the sheet has
  // been refused nothing, and telling them to contact support says otherwise.
  if (surfaceFailure?.code === STRIPE_KYC_CANCELLED) {
    return entryScreen
  }

  if (surfaceFailure !== undefined && flow.page.screen === 'AUTH_CAPTURE') {
    // The provider refused the authorization, or its frame ended on a status
    // this build does not model. A resend edge is the server's offer of a
    // replacement session, and the only retry that reaches a different answer;
    // without one, the way on is a new quote and a fresh sign-in.
    const startOver = { label: t('common.startOver'), onPress: onBackToEntry }
    return (
      <FiatStatusScreen
        tone="failure"
        title={t('fiat.authConnectFailedTitle', {
          provider: providerName(flow.stepResponse?.provider),
        })}
        detail={surfaceFailure.detail ?? t('fiat.authConnectFailedDetail')}
        cta={
          resend === undefined
            ? startOver
            : { label: t('common.tryAgain'), onPress: retrySurface }
        }
        secondaryCta={resend === undefined ? undefined : startOver}
      />
    )
  }

  // Stripe only. A Stripe surface that answered and gave up ends the flow here,
  // on every screen the capture can fail from: "Try again" would re-enter the
  // surface that just refused, which is the loop a failed `l0` repair walks —
  // the call fails the same way every time, and only the buyer gets tired.
  //
  // Scoped to Stripe rather than to every non-network failure, because it is
  // only Stripe's repair that cannot succeed on a second attempt. A provider
  // whose round publishes a resend edge is telling us a fresh session is
  // available, and terminating that one strands a buyer this rule was never
  // written for.
  if (
    surfaceFailure !== undefined &&
    flow.stepResponse?.provider === 'STRIPE' &&
    // KYC only. This screen tells the buyer we could not verify them and offers
    // support as the way out; a payment or session-auth surface that refused has
    // verified nothing, and answering it here strands a buyer whose round had a
    // retry available.
    flow.stepResponse.state.kind === 'KYC' &&
    surfaceFailureReason(surfaceFailure.code) === 'provider'
  ) {
    return (
      <FiatStatusScreen
        tone="failure"
        title={t('fiat.kycUnavailableProviderTitle')}
        detail={
          IS_DEV_BUILD && surfaceFailure.detail !== undefined
            ? surfaceFailure.detail
            : t('fiat.kycUnavailableProvider')
        }
        cta={{ label: t('fiat.contactSupport'), onPress: openKycSupport }}
        onClose={close}
      />
    )
  }

  if (surfaceFailure !== undefined && flow.page.screen !== 'KYC_CAPTURE') {
    return (
      <FiatStatusScreen
        tone="failure"
        title={t('fiat.submitFailed')}
        detail={surfaceFailure.detail}
        cta={{ label: t('common.tryAgain'), onPress: retrySurface }}
        onClose={close}
      />
    )
  }

  // Only while the entry still owns the flow: once the step moves on, the
  // page below is what renders.
  if (contactRound !== undefined && !isContactStepOver) {
    let contactError: string | undefined
    if (phoneReverify) {
      contactError = t('fiat.contactReverifyPhone')
    } else if (contactFailure) {
      const emailOnly =
        contactRound.fields.length === 1 &&
        contactRound.fields[0]?.kind === 'EMAIL'
      contactError = emailOnly
        ? t('fiat.contactEmailRejected')
        : t('fiat.refusalDetails')
    } else if (flow.phase === 'RETRYABLE') {
      contactError = t('fiat.submitFailed')
    }
    return (
      <FiatContactScreen
        fields={contactRound.fields}
        initialContacts={phoneReverify ? contacts : undefined}
        defaultRegion={userIpInfo?.alpha2}
        errorMessage={contactError}
        requiresContactChange={contactFailure}
        isSubmitting={flow.isInFlight || isAccepting}
        defaultCountry={userIpInfo?.alpha2}
        onChangeContact={(field, value) => {
          setCollected((known) => rememberContact(known, field.kind, value))
          flow.setField(field.name, value)
        }}
        onSubmit={() => submitEntry(contactRound)}
        onClose={() => setContactRound(undefined)}
      />
    )
  }

  // The replacement price is already on its way and the entry button is
  // showing its pending state, so a failure screen here would flash and go.
  if (staleQuote.isReplacing) {
    return entryScreen
  }

  // A form that failed to send stays up, wearing the failure as its notice, and
  // Confirm sends it again: replacing it with a failure screen and mounting it
  // afresh afterwards reads as the form closing on the user.
  const isFormRetry =
    flow.phase === 'RETRYABLE' &&
    flow.page.screen === 'KYC_FORM' &&
    !roundOutlivedItsQuote

  if (flow.phase === 'RETRYABLE' && capturedPayment) {
    // The order call failed after the sheet charged the card. Only the same
    // order request may be sent again: it carries the same authorisation and
    // idempotency key, so the backend records the payment that already
    // happened. Asking for a new price here is how a card gets charged twice.
    return (
      <FiatStatusScreen
        tone="waiting"
        title={t('fiat.orderConfirmingTitle')}
        detail={t('fiat.orderConfirmingDetail')}
        isSubmitting={flow.isInFlight}
        cta={{
          label: flow.isInFlight ? t('fiat.submitting') : t('common.tryAgain'),
          onPress: () => void flow.retry(),
        }}
        onClose={close}
      />
    )
  }

  if ((flow.phase === 'RETRYABLE' && !isFormRetry) || isRetrying) {
    // A dead quoteRef cannot be replayed: the retry re-sends the body its
    // idempotency key was minted with, so the same 404 comes back forever.
    // Asking again means asking for a price — fetched while the client owns
    // the quote, and reached by giving the round up once the flow holds one.
    const retry = retryAfterFailure({
      flow,
      staleQuote,
      onGiveUpRound: () => {
        // The dead price is cached and still inside its stated lifetime, so a
        // remount alone would show it again.
        quoteState.dropQuote()
        onBackToEntry()
      },
    })
    return (
      <FiatStatusScreen
        tone="failure"
        title={t('fiat.submitFailed')}
        cta={
          isRetrying
            ? { label: t('fiat.submitting'), onPress: noop }
            : {
                label: t('common.tryAgain'),
                onPress: () => {
                  setIsRetrying(true)
                  retry()
                },
              }
        }
        onClose={close}
      />
    )
  }

  if (otpReceipt.receipt !== undefined) {
    return (
      <FiatOtpContent
        {...otpReceipt.receipt.props}
        code={otpReceipt.receipt.code}
        isSubmitting={false}
        verified
        onClose={onBackToEntry}
      />
    )
  }

  // Said once, on the way out of the form, rather than dropping the buyer back
  // on the amount screen with nothing to show for what they just filled in.
  // Continue hands the amount back, since the check is not what they came for.
  if (kycCompletion !== undefined) {
    return (
      <FiatKycCompletion
        key="kyc-completion"
        outcome={kycCompletion}
        retryDetail={
          kycCompletion === 'hold' ? kycHoldRetryDetail(flow, t) : undefined
        }
        onContinue={onBackToEntry}
      />
    )
  }

  // Held above the switch: the pages this spans are its arms, and each would
  // otherwise draw a status screen over a wait the user cannot act on.
  if (isAwaitingSettlement) {
    return entryScreen
  }

  switch (flow.page.screen) {
    case 'AMOUNT_ENTRY':
    case 'QUOTE_REVIEW':
      return entryScreen

    case 'PAYMENT_CAPTURE': {
      const surface = flow.surface
      const surfaceData = captureSurface
      const control = captureModel
      if (
        surface === undefined ||
        surfaceData === undefined ||
        control === undefined
      ) {
        return (
          <FiatStatusScreen
            tone="failure"
            title={t('fiat.unsupported')}
            onClose={close}
          />
        )
      }
      // A handoff owns the canvas and draws its own chrome: there is no widget
      // to cover, and nothing to wait for before showing it.
      if (control === 'HANDOFF') {
        return (
          <Fragment key={surfaceAttempt}>
            {renderCaptureSurface({
              surface: surfaceData,
              adapters: stripeCaptureAdapters,
              placement: 'SCREEN',
              onReady: noop,
              onFailed: failSurface,
              onClose: close,
              onSettled: markProviderSettled,
              report: (result) => void reportSurface(result),
            })}
          </Fragment>
        )
      }
      // Embedded: the amount screen, with the provider's button already in
      // the action's slot (see `renderSubmit`).
      return entryScreen
    }

    case 'AUTH_CAPTURE': {
      if (isEmbeddedAuth) {
        return entryScreen
      }
      if (flow.stepResponse?.provider === 'STRIPE') {
        const state = flow.stepResponse.state
        const surface =
          state.kind === 'SESSION_AUTH' ? state.surface : undefined
        const surfaceTransition = flow.surface
        if (
          surface !== undefined &&
          surfaceTransition !== undefined &&
          stripeOnramp !== undefined
        ) {
          const stripeAdapter = stripeAuthorizeAdapter(
            stripeOnramp,
            flow.stepResponse,
          )
          const control = captureControl(surface, [stripeAdapter])
          if (control === 'HANDOFF') {
            return (
              <Fragment key={surfaceAttempt}>
                {renderCaptureSurface({
                  surface,
                  adapters: [stripeAdapter],
                  placement: 'SCREEN',
                  onReady: noop,
                  onFailed: failSurface,
                  onClose: close,
                  report: (result) => void flow.reportSurface(result),
                })}
              </Fragment>
            )
          }
        }
      }

      const surfaceData = captureSurface
      if (
        surfaceData === undefined ||
        captureModel === undefined ||
        authResultTransition === undefined
      ) {
        return (
          <FiatStatusScreen
            tone="failure"
            title={t('fiat.unsupported')}
            onClose={onBackToEntry}
          />
        )
      }
      return (
        <ScreenSurfaceCover
          key={surfaceAttempt}
          renderSurface={(onReady) =>
            renderCaptureSurface({
              surface: surfaceData,
              // The same adapters `captureModel` was resolved with. Without
              // them only the default registry is tried, nothing claims an
              // AUTH_COMPONENT surface, and the branch renders null on a
              // screen whose guard already passed.
              adapters: stripeCaptureAdapters,
              placement: 'SCREEN',
              onReady,
              onFailed: failSurface,
              onClose: close,
              // Provider-rendered SESSION_AUTH is still a contract SUBMIT: the
              // surface supplies its declared input and this atomically sends
              // it through the transition instead of waiting on React field
              // state.
              report: (result) => {
                // Remembered before the submit that carries it: the round this
                // returns unmounts the surface and remounts the controller,
                // which would otherwise read the arrival as the buyer's own and
                // reopen the sheet they just closed.
                if (result.outcome === 'Cancelled') {
                  setStripeAuthDeclined(true)
                }
                void flow.submit(authResultTransition, result)
              },
            })
          }
        />
      )
    }

    case 'OTP': {
      if (flow.stepResponse?.provider === 'STRIPE') {
        const authStart = stripeSessionAuthStart(flow.stepResponse, flow.ctas)
        // Both halves are required and either can be missing for unrelated reasons: the SDK binding
        // is absent when the native module did not load, and `authStart` is absent when no CTA
        // carries a verifiedEmail. Falling through is indistinguishable on screen, so name which.
        if (stripeOnramp === undefined || authStart === undefined) {
          logger.warn('connect-rn:fiat:stripe_session_auth_unavailable', {
            stripeOnrampPresent: stripeOnramp !== undefined,
            authStartPresent: authStart !== undefined,
            provider: flow.stepResponse?.provider,
            stateKind: flow.stepResponse?.state.kind,
            ctas: flow.ctas.map(
              (cta: {
                id: string
                endpoint: string
                params?: Record<string, unknown>
              }) =>
                `${cta.id}:${cta.endpoint}:${Object.keys(cta.params ?? {}).join('|')}`,
            ),
          })
        }
        if (stripeOnramp !== undefined && authStart !== undefined) {
          logger.info('connect-rn:fiat:session_auth_provider_selected', {
            provider: flow.stepResponse.provider,
            selected: 'stripe_embedded',
            channel: flow.stepResponse.state.kind,
          })
          return (
            <StripeSessionAuthController
              stripe={stripeOnramp}
              verifiedEmail={authStart.verifiedEmail}
              transition={authStart.transition}
              pendingFallback={entryScreen}
              autoStart={!stripeAuthDeclined}
              onResume={() => setStripeAuthDeclined(false)}
              onSubmit={flow.submit}
              onClose={onBackToEntry}
            />
          )
        }
      }

      // The other side of the same decision. A Stripe round reaches here only by
      // falling through the branch above, and the warn there says why; for every
      // other rail this is the only record of which one drew the round.
      logger.info('connect-rn:fiat:session_auth_provider_selected', {
        provider: flow.stepResponse?.provider,
        selected: 'generic_otp',
        channel: flow.stepResponse?.state.kind,
      })
      const code = otpScreenProps(flow, t, contacts)
      // No CTA, or one that collects nothing: there is no code to answer with.
      if (code === undefined) {
        return (
          <FiatStatusScreen
            tone="failure"
            title={t('fiat.unsupported')}
            onClose={onBackToEntry}
          />
        )
      }
      // Keyed on the round: a session that asks for a second channel is a
      // fresh code, and the digits typed for the first must not still be in
      // the boxes.
      //
      // Back gives the round up and lands on the amount, the way the contact
      // step before it does — leaving the flow entirely is the sheet's own
      // dismissal, not a step's back.
      return (
        <FiatOtpScreen
          key={flow.page.identity}
          {...otpReceipt.bind(code)}
          onClose={onBackToEntry}
        />
      )
    }

    case 'KYC_FORM': {
      const identity = formScreenProps(
        flow,
        t,
        userIpInfo?.alpha2,
        carriedKycValues,
      )
      // Nothing to walk, or no CTA to send it with: not a form.
      if (identity === undefined) {
        return (
          <FiatStatusScreen
            tone="failure"
            title={t('fiat.unsupported')}
            onClose={onBackToEntry}
          />
        )
      }
      // Stripe's account is minted by its own SDK, so this round's Confirm
      // registers the Link user before the answers are sent. Absent for every
      // other round, whose delivery the backend makes itself.
      const isStripe =
        flow.stepResponse?.provider === 'STRIPE' && stripeOnramp !== undefined
      // The round names itself on the CTA the form sends with; nothing is
      // inferred from which fields it carries. Read untyped, like the email
      // on SESSION_AUTH, so an unnamed round submits without a Stripe push.
      const kycRoundParams = flow.ctas[0]?.params
      const kycRound = kycRoundParams?.kycRound
      const verifiedEmail = kycRoundParams?.verifiedEmail
      const registration =
        isStripe && kycRound === 'ACCOUNT_CREATION'
          ? stripeLinkRegistrationFrom(
              identity.values,
              typeof verifiedEmail === 'string' ? verifiedEmail : undefined,
            )
          : undefined
      const kycInfo =
        isStripe && kycRound === 'IDENTITY'
          ? stripeKycInfoFrom(identity.values)
          : undefined
      const pushToStripe =
        stripeOnramp === undefined
          ? undefined
          : registration !== undefined
            ? () => stripeOnramp.registerLinkUser(registration)
            : kycInfo !== undefined
              ? () => stripeOnramp.attachKycInfo(kycInfo)
              : undefined
      // A Stripe round that reaches the backend without reaching Stripe is invisible from both
      // sides: the form posts 200, the provider re-asks, and the buyer loops with nothing logged
      // anywhere. Named on the attempt itself — reporting it as the screen rendered called an
      // untouched form a skip, once per keystroke.
      const reportSkippedPush = () => {
        // On the provider, not on `isStripe`. `isStripe` also requires the
        // onramp client, so the case this log exists to explain — a Stripe
        // round with no client — was the one case it stayed silent for.
        if (
          flow.stepResponse?.provider !== 'STRIPE' ||
          kycRound === undefined
        ) {
          return
        }
        logger.warn('connect-rn:fiat:stripe_kyc_push_skipped', {
          round: kycRound,
          hasOnrampClient: stripeOnramp !== undefined,
          hasVerifiedEmail: typeof verifiedEmail === 'string',
          blankValues: Object.entries(identity.values)
            .filter(
              ([, value]) => typeof value !== 'string' || value.trim() === '',
            )
            .map(([key]) => key),
          suppliedValues: Object.keys(identity.values),
        })
      }
      const submitIdentity =
        pushToStripe === undefined
          ? () => {
              reportSkippedPush()
              carryKycValues(identity.values)
              identity.onSubmit()
            }
          : () => {
              // Narrowed, and only a named round can block: `kycRound` is untyped params, and an
              // unnamed one comparing equal to an unset ref would block the very first push.
              // Unreachable while `pushToStripe` exists only for a named round, and fails open.
              const pushRound =
                typeof kycRound === 'string' ? kycRound : undefined
              // A tap landing while the push is still running is dropped. The
              // round marker is set before the push resolves, so without this
              // the skip branch below submitted to the backend before Stripe
              // held the values — and `flow.isInFlight` only goes true once
              // `onSubmit` runs, so Confirm stays live for that whole window.
              if (stripePushInFlight.current) {
                return
              }
              // Only the push is skipped, not the submit. The backend re-asks
              // the same round when a field comes back refused, and returning
              // from the whole handler swallowed that second answer: the form
              // sat there, Confirm did nothing, and the round never moved.
              // Stripe already holds this round's values, so repeating the push
              // is what must not happen — sending the answers still must.
              if (
                pushRound !== undefined &&
                stripePushedRound.current === pushRound
              ) {
                carryKycValues(identity.values)
                identity.onSubmit()
                return
              }
              stripePushedRound.current = pushRound
              stripePushInFlight.current = true
              setStripeRegistrationError(undefined)
              void (async () => {
                let pushed = false
                try {
                  const result = await pushToStripe()
                  if (result.error !== undefined) {
                    const isRegistration = registration !== undefined
                    // Every field Stripe gives us. `code` alone is "Failed" for anything it does not
                    // have a specific code for, which is what a refused `attachKycInfo` looks like —
                    // so the one field we used to log was the one that never said why.
                    logger.warn('connect-rn:fiat:stripe_kyc_push_failed', {
                      call: isRegistration
                        ? 'registerLinkUser'
                        : 'attachKycInfo',
                      code: result.error.code,
                      onrampErrorType: result.error.onrampErrorType,
                      stripeErrorCode: result.error.stripeErrorCode,
                      apiErrorCode: result.error.apiErrorCode,
                      apiErrorType: result.error.apiErrorType,
                      developerMessage: result.error.developerMessage,
                      requestId: result.error.requestId,
                    })
                    setStripeRegistrationError(
                      isRegistration
                        ? t('fiat.stripeRegistrationFailed')
                        : t('fiat.stripeKycInfoFailed'),
                    )
                    return
                  }
                  pushed = true
                  carryKycValues(identity.values)
                  identity.onSubmit()
                } catch (error: unknown) {
                  // A rejection, not a resolved `{ error }` — Stripe's client throws when the SDK
                  // never initialized. Detached, so nothing else would ever see it and the buyer
                  // would sit on an unchanged form.
                  logger.warn('connect-rn:fiat:stripe_kyc_push_threw', {
                    round: kycRound,
                    reason: error instanceof Error ? error.message : 'unknown',
                  })
                  setStripeRegistrationError(
                    registration !== undefined
                      ? t('fiat.stripeRegistrationFailed')
                      : t('fiat.stripeKycInfoFailed'),
                  )
                } finally {
                  stripePushInFlight.current = false
                  // Only a push that never landed re-arms this round. Once Stripe
                  // has the registration, repeating it can only fail.
                  if (!pushed) {
                    stripePushedRound.current = undefined
                  }
                }
              })()
            }
      return (
        <FiatIdentityStep
          {...identity}
          errorMessage={
            stripeRegistrationError ??
            (isFormRetry || kycProviderFailure
              ? undefined
              : roundErrorMessage(roundError, t, flow.stepResponse?.provider))
          }
          // Under the failed send's own key while nothing has changed since:
          // a server that took it and lost the answer must see one attempt.
          onSubmit={isReplayable ? () => void flow.retry() : submitIdentity}
          onClose={close}
        />
      )
    }

    // Identity collected by the provider's own SDK. It owns the canvas, so
    // there is no widget to cover and nothing to wait for before showing it.
    case 'KYC_CAPTURE': {
      const surface = flow.surface
      const surfaceData = captureSurface
      if (
        surface === undefined ||
        surfaceData === undefined ||
        captureModel === undefined
      ) {
        return (
          <FiatStatusScreen
            tone="failure"
            title={t('fiat.unsupported')}
            onClose={onBackToEntry}
          />
        )
      }
      return (
        <FiatDocumentCaptureStep
          key={flow.page.identity}
          kycProvider={
            surfaceData.kind === 'KYC_SDK' ? surfaceData.provider : undefined
          }
          onBack={onBackToEntry}
        >
          {() => (
            <View
              key={surfaceAttempt}
              pointerEvents={surfaceFailure === undefined ? 'auto' : 'none'}
            >
              {renderCaptureSurface({
                surface: surfaceData,
                adapters: [
                  sumsubKycAdapter(renewCaptureToken),
                  ...stripeCaptureAdapters,
                ],
                placement: 'SCREEN',
                onReady: noop,
                onFailed: failSurface,
                onClose: close,
                report: (result) => {
                  if (surfaceFailure === undefined) {
                    void reportSurface(result)
                  }
                },
              })}
            </View>
          )}
        </FiatDocumentCaptureStep>
      )
    }

    // A resolved check is no longer a screen of its own: the buyer meets the
    // verdict back on the amount they entered, in the banner and the button.
    case 'KYC_VERIFYING':
    case 'KYC_ON_HOLD':
    case 'KYC_REJECTED':
      return entryScreen

    case 'ORDER_REVIEW':
      return (
        <FiatStatusScreen
          tone="waiting"
          title={t('fiat.reviewTitle')}
          detail={reviewDetail(flow)}
          cta={recoveryCta(flow, t)}
          onClose={close}
        />
      )

    case 'ORDER_PENDING':
      return (
        <FiatPendingOrderScreen
          flow={flow}
          onResumeAmount={onChangeAmount}
          onClose={close}
        />
      )

    // Only reached without a capture of this session's own — a resumed order
    // the buyer is watching rather than one they just paid for.
    case 'ORDER_TRACKING':
      return (
        <FiatStatusScreen
          tone="waiting"
          title={t('fiat.processing')}
          onClose={close}
        />
      )

    // The direct route is already closing, with its confirmation up — another
    // faceplate would flash for the frame in between. A swept order has no
    // such confirmation, so it keeps this one.
    case 'SUCCESS':
      return isDirect ? (
        entryScreen
      ) : (
        <FiatStatusScreen
          tone="success"
          title={t('fiat.success')}
          detail={t('fiat.successDetail')}
          onClose={close}
        />
      )

    // A failure of the order the buyer just paid for lands back on the amount
    // screen (see `orderFailed`); one reached any other way keeps its screen.
    case 'FAILURE':
      if (orderFailed) {
        return entryScreen
      }
      return (
        <FiatStatusScreen
          tone="failure"
          title={t('fiat.failure')}
          cta={recoveryCta(flow, t)}
          onClose={close}
        />
      )

    case 'REFUND':
      return (
        <FiatStatusScreen
          tone="failure"
          title={t('fiat.refunded')}
          onClose={close}
        />
      )

    case 'BLOCKED':
      // A region the backend will not serve reads the same whether the check
      // ran here or there, so a late verdict lands on the gate's own screen.
      // Unless it names a comeback date: that is a wait, and the status screen
      // is the one that can say when.
      if (
        blockedState?.reason === 'REGION_UNSUPPORTED' &&
        typeof blockedState.retryAfter !== 'string'
      ) {
        return <FiatRegionUnsupportedScreen onClose={close} />
      }
      return (
        <FiatStatusScreen
          tone="failure"
          title={t('fiat.blocked')}
          detail={blockedRetryDetail(flow)}
          onClose={close}
        />
      )

    default:
      return (
        <FiatStatusScreen
          tone="failure"
          title={t('fiat.unsupported')}
          onClose={onBackToEntry}
        />
      )
  }
}
/**
 * What "Try again" does with the failure on screen. A live ref replays; a dead
 * one needs a price, which only the side that owns the quote can ask for.
 */
/**
 * What the identity banner reports: a resolved check, or one the backend is
 * still asking for.
 */
type KycNoticeState = KycOutcome | 'complete' | 'required'

/**
 * The outcomes the deposit screen reports, one literal key per branch —
 * `t()` never takes a computed key (`.ast-grep/rules/i18n-t-string-literal.yml`).
 */
function kycOutcomeCopy(
  outcome: KycNoticeState,
  t: TFunction,
  /** A cooling-off hold names the day to come back; the generic line can't. */
  holdDetail: string | undefined,
): { title: string; detail: string } {
  switch (outcome) {
    case 'required':
      return {
        title: t('fiat.kycRequiredTitle'),
        detail: t('fiat.kycRequiredDetail'),
      }
    case 'complete':
      return {
        title: t('fiat.kycCompleteTitle'),
        detail: t('fiat.kycCompleteDetail'),
      }
    case 'pending':
      return {
        title: t('fiat.kycPendingTitle'),
        detail: t('fiat.kycPendingDetail'),
      }
    case 'soft':
      return {
        title: t('fiat.kycSoftTitle'),
        detail: holdDetail ?? t('fiat.kycSoftDetail'),
      }
    case 'hard':
      return { title: t('fiat.kycHardTitle'), detail: t('fiat.kycHardDetail') }
  }
}

// Coinbase KYC does not raise the deposit max — it lifts the limit on lifetime
// transactions instead, so offering the route does not help the buyer for now.
// TODO: remove when we have more integrations.
const SHOW_DEPOSIT_MAX_ROUTE: boolean = false

/**
 * The routes out of a held deposit, in the order the frames list them.
 *
 * "Buy tokens directly" is descoped from launch (ENG-6095) and drawn nowhere.
 * A re-do is offered only while the response still publishes the transition
 * that restarts the check, so the client never invents a resubmit.
 */
function kycOptionRows({
  outcome,
  depositMax,
  t,
  onRedo,
  onDepositMax,
  onSupport,
  onClose,
}: {
  outcome: KycOutcome | 'complete' | undefined
  /** The ceiling as the row words it, when one is known. */
  depositMax: string | undefined
  t: TFunction
  onRedo: (() => void) | undefined
  onDepositMax: () => void
  onSupport: () => void
  onClose: () => void
}): FiatKycOptionRowProps[] | undefined {
  if (outcome === undefined || outcome === 'complete') {
    return undefined
  }

  const go = t('common.go')
  if (outcome === 'hard') {
    return [
      {
        title: t('fiat.kycOptionSupportTitle'),
        detail: t('fiat.kycOptionSupportDetail'),
        action: go,
        detailWidth: 211,
        onPress: onSupport,
      },
    ]
  }

  const rows: FiatKycOptionRowProps[] = []
  if (outcome === 'soft' && onRedo !== undefined) {
    rows.push({
      title: t('fiat.kycOptionRedoTitle'),
      detail: t('fiat.kycOptionRedoDetail'),
      action: go,
      detailWidth: 214,
      onPress: onRedo,
    })
  }
  if (SHOW_DEPOSIT_MAX_ROUTE) {
    // The ceiling belongs to the tier, not to a price, so it is not always
    // known here; the row is drawn either way and words itself for what it has.
    rows.push({
      title: t('fiat.kycOptionMaxTitle'),
      detail:
        depositMax === undefined
          ? t('fiat.kycOptionMaxDetailUnknown')
          : t('fiat.kycOptionMaxDetail', { max: depositMax }),
      action: go,
      detailWidth: 200,
      onPress: onDepositMax,
    })
  }
  // The card is the whole screen, so a version of it with no route on it would
  // strand the buyer behind the back button alone.
  if (rows.length === 0) {
    rows.push({
      title: t('fiat.kycOptionCloseTitle'),
      detail: t('fiat.kycOptionCloseDetail'),
      action: go,
      detailWidth: 211,
      onPress: onClose,
    })
  }
  return rows
}

/**
 * What "Re-do ID verification" does. A round still holding its form carries a
 * SUBMIT that expects the values, so sending it bare would post an empty form;
 * entering the round is what re-opens it.
 */
function kycRedoRoute({
  awaitsForm,
  openEntryRound,
  transition,
  flow,
}: {
  awaitsForm: boolean
  openEntryRound: () => void
  transition: SubmitTransition | undefined
  flow: FiatFlow
}): (() => void) | undefined {
  if (awaitsForm) {
    return openEntryRound
  }
  if (transition === undefined) {
    return undefined
  }
  return () => void flow.submit(transition)
}

/** The card's headline, one literal key per outcome. */
function kycOptionsTitle(
  outcome: KycOutcome | 'complete' | undefined,
  t: TFunction,
): string {
  switch (outcome) {
    case 'soft':
      return t('fiat.kycOptionsSoftTitle')
    case 'hard':
      return t('fiat.kycOptionsHardTitle')
    default:
      return t('fiat.kycOptionsPendingTitle')
  }
}

/** What the button says once the check resolves; the quote's own label until then. */
function kycOutcomeCta(
  outcome: KycNoticeState | undefined,
  t: TFunction,
): string | undefined {
  switch (outcome) {
    case 'required':
      return t('fiat.kycRequiredCta')
    case 'pending':
      return t('fiat.kycPendingCta')
    case 'soft':
      return t('fiat.kycSoftCta')
    case 'hard':
      return t('fiat.kycHardCta')
    // A passed check pays with the quote's own label.
    default:
      return undefined
  }
}

const styles = StyleSheet.create({
  // Over the header, at the inset the frames give it (2387:11504).
  outcomeBanner: { position: 'absolute', top: 81, left: 14, right: 14 },
  // Higher than the identity banner — 2812:1135 starts it at the safe area,
  // so its own top rides on the device's inset rather than a fixed offset.
  failureBanner: { position: 'absolute', left: 14, right: 14 },
})

/** The provider's button as the slot draws it, whichever source carried it. */
interface EmbeddedSlot {
  /** Identity of the attempt: a change here remounts the provider's page. */
  key: string
  surface: Surface
  /** Set when a quote carried the control, so the report has a transition. */
  transition?: SurfaceTransition
}

/**
 * The embedded surface on screen, from a capture state or from the quote's own
 * control. Keyed exactly as each renderer keys it, so the held slot and the
 * live one are the same slot.
 */
function embeddedSlot({
  captureSurface,
  quoteControl,
  shownQuoteRef,
  surfaceAttempt,
}: {
  captureSurface: Surface | undefined
  quoteControl: { surface: Surface; transition: SurfaceTransition } | undefined
  shownQuoteRef: string | undefined
  surfaceAttempt: number
}): EmbeddedSlot | undefined {
  if (captureSurface !== undefined) {
    return { key: `capture:${surfaceAttempt}`, surface: captureSurface }
  }
  if (
    quoteControl === undefined ||
    captureControl(quoteControl.surface) !== 'EMBEDDED'
  ) {
    return undefined
  }
  const { surface, transition } = quoteControl
  return {
    key: `${shownQuoteRef ?? surface.kind}:${surfaceAttempt}`,
    surface,
    transition,
  }
}

/**
 * The order's id, wherever the response put it: the contract leaves the
 * placement open between the envelope and the order the state carries.
 */
function orderIdOf(flow: FiatFlow): string | undefined {
  const response = flow.stepResponse
  if (response === undefined) {
    return undefined
  }
  if (response.orderId !== undefined) {
    return response.orderId
  }
  const { state } = response
  return state.kind === 'ORDER' && state.phase === 'CREATED'
    ? state.order?.orderId
    : undefined
}

/**
 * What the buyer paid, formatted: the created order's own figure, or the price
 * they were shown. `undefined` while neither has landed.
 */
function paidAmount(
  flow: FiatFlow,
  quote: Quote | undefined,
): string | undefined {
  const state = flow.stepResponse?.state
  const paid =
    (state?.kind === 'ORDER' && state.phase === 'CREATED'
      ? state.order?.fiat
      : undefined) ?? quote?.fiat
  return paid === undefined
    ? undefined
    : formatFiat(Number(paid.amount), paid.currency)
}

/**
 * The rail as the host's callback names it. `undefined` for a method this
 * type does not carry — the callback is skipped rather than guessed at.
 */
function depositMethodType(
  paymentMethod: string,
): DepositMethod['type'] | undefined {
  return paymentMethod === 'apple_pay' || paymentMethod === 'card'
    ? paymentMethod
    : undefined
}

/** The quote a KYC round is bound to, when the flow holds one. */
function captureQuoteRefOf(flow: FiatFlow): string | undefined {
  const state = flow.stepResponse?.state
  return state?.kind === 'KYC' ? state.quote?.quoteRef : undefined
}

function retryAfterFailure({
  flow,
  staleQuote,
  onGiveUpRound,
}: {
  flow: FiatFlow
  staleQuote: StaleQuoteRecovery
  onGiveUpRound: () => void
}): () => void {
  if (!isStaleQuoteFailure(flow.submitError)) {
    return () => void flow.retry()
  }
  return flow.stepResponse === undefined
    ? staleQuote.replaceQuote
    : onGiveUpRound
}

function createFiatSubmitRenderer({
  amount,
  entryCta,
  isEntryReady,
  flowRefused,
  paymentUnavailable,
  onRetryFlowRefusal,
  onRetryUnavailable,
  shownQuoteRef,
  quoteState,
  flow,
  reportSurface,
  t,
  canSubmitQuote,
  depositAddressUnavailable,
  onRetryDepositAddress,
  surfaceAttempt,
  onSurfaceFailed,
  onSurfaceReadyChange,
  onSettled,
  onClose,
}: {
  amount: string
  entryCta: EntryCta
  isEntryReady: boolean
  flowRefused: boolean
  paymentUnavailable: boolean
  onRetryFlowRefusal: () => void
  onRetryUnavailable: () => void
  /** The round the screen is drawing, so a remount rebuilds its surface. */
  shownQuoteRef: string | undefined
  quoteState: UseFiatQuoteResult
  flow: FiatFlow
  /** The flow's own report, wrapped so the step learns of a completed capture. */
  reportSurface: (
    result: SurfaceResult,
    transition?: SurfaceTransition,
  ) => Promise<void>
  t: TFunction
  /** Whether a tap on the app's own action would do anything. */
  canSubmitQuote: boolean
  depositAddressUnavailable: boolean
  onRetryDepositAddress: () => void
  surfaceAttempt: number
  onSurfaceFailed: (code: string, detail?: string) => void
  onSurfaceReadyChange: (ready: boolean) => void
  /** The rail says the crypto is sent. See {@link SurfaceContext.onSettled}. */
  onSettled: () => void
  onClose: () => void
}): (() => ReactNode) | undefined {
  if (!isQuotableAmount(amount)) {
    return () => (
      <PillButton
        label={t('depositAmount.enterAmount')}
        size="buttonLg"
        variant="secondary"
        disabled
        onPress={noop}
      />
    )
  }
  if (!isEntryReady && flowRefused) {
    return () => (
      <PillButton
        label={t('common.tryAgain')}
        size="buttonLg"
        variant="secondary"
        onPress={onRetryFlowRefusal}
      />
    )
  }
  // Replaying the same below-minimum quote cannot succeed. Keep one action-shaped
  // element on screen and let its disabled label explain what needs to change.
  if (!isEntryReady && quoteState.minimum !== undefined) {
    const { amount: minimumAmount, currency } = quoteState.minimum
    return () => (
      <PillButton
        label={t('depositAmount.minimumAmount', {
          amount: formatFiatWhole(Number(minimumAmount), currency),
        })}
        size="buttonLg"
        variant="secondary"
        disabled
        onPress={noop}
      />
    )
  }
  if (
    !isEntryReady &&
    (paymentUnavailable || quoteState.quoteError !== undefined)
  ) {
    return () => (
      <PillButton
        label={t('common.tryAgain')}
        size="buttonLg"
        variant="secondary"
        onPress={
          paymentUnavailable ? onRetryUnavailable : quoteState.retryQuote
        }
      />
    )
  }
  if (!isEntryReady) {
    return () => (
      <SurfaceSlot label={t('common.loading')} size={SURFACE_MATCHED_SIZE} />
    )
  }
  const ownControl = surfaceControl(entryCta)
  if (ownControl === undefined) {
    // Handing the slot back is what lets the app draw its own action — but a
    // pill that cannot be pressed draws the host's action color as a ghost, so
    // the wait and the failure keep their usual shapes instead.
    if (depositAddressUnavailable) {
      return () => (
        <PillButton
          label={t('common.tryAgain')}
          size="buttonLg"
          variant="secondary"
          onPress={onRetryDepositAddress}
        />
      )
    }
    return canSubmitQuote
      ? undefined
      : () => (
          <SurfaceSlot
            label={t('common.loading')}
            size={SURFACE_MATCHED_SIZE}
          />
        )
  }

  const { surface } = ownControl
  // A handoff draws a full screen and opens a browser on sight, so the entry
  // screen's button slot cannot host one. No rail emits a handoff on a quote
  // today; refusing keeps a future one out of the wrong slot.
  const control = captureControl(surface)
  if (control !== 'EMBEDDED') {
    return () => (
      <PillButton
        label={t('fiat.unsupported')}
        size="buttonLg"
        variant="secondary"
        disabled
        onPress={noop}
      />
    )
  }

  const draw = (onReady: () => void) =>
    renderCaptureSurface({
      surface,
      placement: 'INLINE',
      onReady,
      onFailed: onSurfaceFailed,
      onClose,
      onSettled,
      // The quote drew this surface, so the reducer has no transition to look
      // up; the one the quote carried has to be handed over.
      report: (result) => void reportSurface(result, ownControl.transition),
    })

  return () => (
    <SurfaceSlot
      surfaceKey={`${shownQuoteRef ?? surface.kind}:${surfaceAttempt}`}
      label={t('common.loading')}
      size={SURFACE_MATCHED_SIZE}
      onReadyChange={onSurfaceReadyChange}
      renderSurface={draw}
    />
  )
}
